import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  MdClose,
  MdFileUpload,
  MdCheckCircle,
  MdLink,
  MdLocationOn,
  MdMyLocation,
} from "react-icons/md";
import { FiDownload } from "react-icons/fi";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import Select from "react-select";
import moment from "moment";
import Papa from "papaparse";
import ExcelJS from "exceljs";
import * as XLSX from "xlsx";
import { Upload, message } from "antd";
import {
  searchPlaces,
  getPlaceDetails,
  getOlaPublicBrowserKey,
  reverseGeocodePlace,
} from "../../../../Service/OlaMapsService";
import {
  checkPhoneExists,
  createVisitForPatient,
  getCancellationPolicyDisclosure,
  getPatientById,
  getPartner,
} from "../../../../Service/partnerAPI";
import { getLookupData } from "../../../../Service/lookup";
import { showToast } from "../../../../reusableUI/toaster";
import { getFreshTransientUiState } from "./addPatientModalState";

const { Dragger } = Upload;

const inputClass =
  "w-full bg-[#F6F8FC] rounded-xl px-4 py-2.5 text-[13px] font-poppins border border-gray-200 outline-none focus:border-[#3E57E8] focus:ring-2 focus:ring-[#3E57E8]/15 text-gray-700 transition-all";

// Human window copy for a server-provided cancellation policy tier. The
// server owns the numbers; this only phrases them.
const formatPolicyMinutes = (minutes) => {
  if (minutes % 1440 === 0) {
    const days = minutes / 1440;
    return days === 1 ? "24 hours" : `${days} days`;
  }
  if (minutes % 60 === 0) return `${minutes / 60} hours`;
  return `${minutes} minutes`;
};

const cancellationTierWindowCopy = (minimumMinutes, tiers = []) => {
  const larger = (tiers || [])
    .filter((tier) => tier.minimumMinutesInclusive > minimumMinutes)
    .sort((a, b) => a.minimumMinutesInclusive - b.minimumMinutesInclusive)[0];
  if (!larger) {
    const window = formatPolicyMinutes(minimumMinutes);
    return `${window[0].toUpperCase()}${window.slice(1)} or more before the first service`;
  }
  if (minimumMinutes === 0) {
    return `Less than ${formatPolicyMinutes(larger.minimumMinutesInclusive)} before the first service`;
  }
  return `From ${formatPolicyMinutes(minimumMinutes)} to less than ${formatPolicyMinutes(
    larger.minimumMinutesInclusive,
  )} before the first service`;
};

const reactSelectStyles = {
  control: (base, state) => ({
    ...base,
    backgroundColor: "#F6F8FC",
    borderRadius: "0.75rem",
    minHeight: 44,
    borderColor: state.isFocused ? "#3E57E8" : "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(62,87,232,0.15)" : "none",
    ":hover": { borderColor: "#3E57E8" },
    fontSize: 13,
  }),
  valueContainer: (base) => ({ ...base, padding: "2px 12px" }),
  placeholder: (base) => ({ ...base, color: "#9CA3AF", fontSize: 13 }),
  multiValue: (base) => ({ ...base, backgroundColor: "#E7EEFF" }),
  multiValueLabel: (base) => ({ ...base, fontSize: 12, color: "#1F2937" }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
};

const formatServiceOptionWithNotes = (option, { context }) => {
  if (context !== "menu") return option.label;
  const notes = String(option?.notes || "").trim();
  return (
    <div className="flex flex-col">
      <span>{option.label}</span>
      {notes ? (
        <span className="text-[11px] text-gray-500 italic mt-0.5">{notes}</span>
      ) : null}
    </div>
  );
};

const safeArr = (v) => (Array.isArray(v) ? v : []);
const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
// Partner pricing APIs already return net fees after discount calculation.
const resolvePartnerFee = (rawFee) => toNumber(rawFee);
const NAME_REGEX = /^[A-Za-z ]{2,50}$/;
const isValidName = (name) => {
  if (!name) return false;
  return NAME_REGEX.test(String(name).trim());
};
const hasValidCoordinates = (coordinates) => {
  if (!coordinates || typeof coordinates !== "object") return false;
  const lat = Number(coordinates?.lat);
  const lng = Number(coordinates?.lng);
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
};

const DEFAULT_MAP_CENTER = { lat: 13.0827, lng: 80.2707 };
const GOOGLE_MAP_HOSTS = new Set([
  "google.com",
  "www.google.com",
  "maps.google.com",
  "google.co.in",
  "www.google.co.in",
  "maps.google.co.in",
]);
const GOOGLE_SHORT_LINK_HOSTS = new Set(["maps.app.goo.gl", "goo.gl"]);
const COORDINATE_PAIR_REGEX =
  /(-?\d{1,2}(?:\.\d+)?)\s*,\s*(-?\d{1,3}(?:\.\d+)?)/;

const toCoordinatePair = (latValue, lngValue) => {
  const coordinates = { lat: Number(latValue), lng: Number(lngValue) };
  return hasValidCoordinates(coordinates) ? coordinates : null;
};

const parseCoordinatePair = (value) => {
  const match = String(value || "").match(COORDINATE_PAIR_REGEX);
  return match ? toCoordinatePair(match[1], match[2]) : null;
};

const parseGoogleMapsCoordinates = (rawLink) => {
  const value = String(rawLink || "").trim();
  if (!value) return { error: "Enter a Google Maps link." };

  let url;
  try {
    url = new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`);
  } catch (error) {
    return { error: "Enter a valid Google Maps URL." };
  }

  if (url.protocol !== "https:") {
    return { error: "Only secure HTTPS Google Maps links are allowed." };
  }

  const hostname = url.hostname.toLowerCase();
  if (GOOGLE_SHORT_LINK_HOSTS.has(hostname)) {
    return {
      error:
        "Short Google Maps links do not contain coordinates. Open the link, copy the full browser URL, and paste it here.",
    };
  }
  if (!GOOGLE_MAP_HOSTS.has(hostname)) {
    return { error: "Only Google Maps links are allowed." };
  }

  let decodedUrl = url.toString();
  try {
    decodedUrl = decodeURIComponent(decodedUrl);
  } catch (error) {
    // The original URL can still be parsed when it contains malformed escapes.
  }

  const dataCoordinates = decodedUrl.match(
    /!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/,
  );
  if (dataCoordinates) {
    const coordinates = toCoordinatePair(
      dataCoordinates[1],
      dataCoordinates[2],
    );
    if (coordinates) return { coordinates };
  }

  const alternateDataCoordinates = decodedUrl.match(
    /!2d(-?\d+(?:\.\d+)?)!3d(-?\d+(?:\.\d+)?)/,
  );
  if (alternateDataCoordinates) {
    const coordinates = toCoordinatePair(
      alternateDataCoordinates[2],
      alternateDataCoordinates[1],
    );
    if (coordinates) return { coordinates };
  }

  const coordinateParams = ["query", "q", "ll", "center", "destination"];
  for (const param of coordinateParams) {
    const coordinates = parseCoordinatePair(url.searchParams.get(param));
    if (coordinates) return { coordinates };
  }

  const viewportCoordinates = decodedUrl.match(
    /@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)(?:,|\/|$)/,
  );
  if (viewportCoordinates) {
    const coordinates = toCoordinatePair(
      viewportCoordinates[1],
      viewportCoordinates[2],
    );
    if (coordinates) return { coordinates };
  }

  return {
    error:
      "Coordinates were not found in this link. In Google Maps, drop a pin at the patient's location and copy the full browser URL.",
  };
};

const mapAddressDetails = (details, coordinates) => {
  if (!details) return null;
  const components = Array.isArray(details.address_components)
    ? details.address_components
    : [];
  let locality = "";
  let postalTown = "";
  let administrativeAreaLevel2 = "";
  let state = "";
  let country = "";
  let pincode = "";

  components.forEach((component) => {
    const types = Array.isArray(component.types) ? component.types : [];
    const value = component.long_name || component.short_name || "";
    if (types.includes("locality") || types.includes("sublocality"))
      locality = value;
    if (types.includes("postal_town")) postalTown = value;
    if (types.includes("administrative_area_level_2"))
      administrativeAreaLevel2 = value;
    if (types.includes("administrative_area_level_1")) state = value;
    if (types.includes("country")) country = value;
    if (types.includes("postal_code")) pincode = value;
  });

  const city =
    postalTown && locality && postalTown !== locality
      ? `${postalTown}, ${locality}`
      : postalTown || locality || administrativeAreaLevel2;

  return {
    address: details.formatted_address || details.description || "",
    city,
    district: administrativeAreaLevel2 || locality,
    state,
    country: country || "India",
    pincode,
    coordinates,
  };
};

const reverseGeocodeCoordinates = async (coordinates) => {
  if (!hasValidCoordinates(coordinates)) return null;

  try {
    const details = await reverseGeocodePlace(coordinates);
    return mapAddressDetails(details, coordinates);
  } catch (error) {
    console.error("Ola reverse geocoding failed:", error);
    return null;
  }
};
const getDobBounds = () => {
  const todayMoment = moment().startOf("day");
  return {
    minDobMoment: todayMoment.clone().subtract(100, "years"),
    maxDobMoment: todayMoment,
  };
};
const formatDobBound = (dobMoment) =>
  dobMoment && typeof dobMoment.isValid === "function" && dobMoment.isValid()
    ? dobMoment.format("YYYY-MM-DD")
    : "";
const normalizePartnerServiceToken = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
const extractPartnerServiceString = (service) => {
  if (typeof service === "string") return service.trim();
  if (service && typeof service === "object") {
    return String(
      service?.value ??
        service?.name ??
        service?.label ??
        service?.service ??
        service?.type ??
        "",
    ).trim();
  }
  return "";
};
const mapPartnerServicesToCareCategories = (services = []) => {
  const categories = [];
  const addCategory = (value, label) => {
    if (!categories.some((item) => item.value === value)) {
      categories.push({ value, label });
    }
  };

  safeArr(services).forEach((service) => {
    const normalized = normalizePartnerServiceToken(
      extractPartnerServiceString(service),
    );
    if (!normalized) return;

    if (
      normalized.includes("nurse") ||
      normalized.includes("nursing assistant") ||
      normalized.includes("nursing-assistant")
    ) {
      addCategory("Nursing Care", "Nursing Care");
    }

    if (
      normalized.includes("physio") ||
      normalized.includes("physiotherapist") ||
      normalized.includes("physiotherapy")
    ) {
      addCategory("Physio", "Physiotherapy");
    }
  });

  return categories;
};

const shiftLabel = (shift) => {
  if (shift === "morning") return "Morning";
  if (shift === "night") return "Night";
  if (shift === "24/7") return "24/7";
  return shift || "";
};

const dutyKey = (shift, type) => `${shift}__${type}`;
const regularSlotKey = (shift, type, serviceName, index = "") =>
  `${shift}__${type}__${String(serviceName || "")
    .trim()
    .toLowerCase()}__${index}`;
const normalizeRegularRole = (value) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-");
  if (normalized === "nursing-assistant" || normalized === "nursingassistant") {
    return "nursing-assistant";
  }
  return "nurse";
};
const MAX_PDF_SIZE_BYTES = 10 * 1024 * 1024;
const BULK_REQUIRED_HEADERS = [
  "mrn",
  "title",
  "name",
  "dob",
  "gender",
  "phone",
];
const BULK_ALLOWED_TITLES = ["Mr", "Mrs", "Ms", "Dr"];
const BULK_ALLOWED_GENDERS = ["male", "female"];
const BULK_TITLE_MAP = {
  mr: "Mr",
  mrs: "Mrs",
  ms: "Ms",
  dr: "Dr",
};
const BULK_GENDER_MAP = {
  male: "male",
  female: "female",
};

const normalizeBulkHeader = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();
const normalizeBulkName = (value) =>
  String(value || "")
    .replace(/\s+/g, " ")
    .trim();
const normalizeBulkDigits = (value) => String(value || "").replace(/\D/g, "");
const isValidBulkPhone = (value) => {
  const digits = normalizeBulkDigits(value);
  return digits.length === 12 && digits.startsWith("91");
};
const normalizeBulkPhone = (value) => normalizeBulkDigits(value);
const normalizeBulkTitle = (value) => {
  const key = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\./g, "");
  return BULK_TITLE_MAP[key] || "";
};
const normalizeBulkGender = (value) => {
  const key = String(value || "")
    .trim()
    .toLowerCase();
  return BULK_GENDER_MAP[key] || "";
};
const parseBulkDob = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return moment(value).startOf("day");
  }
  const raw = String(value || "").trim();
  if (!raw) return null;
  const parsed = moment(raw, "DD/MM/YYYY", true);
  if (!parsed.isValid()) return null;
  return parsed.startOf("day");
};
const isBulkDobInRange = (dobMoment) => {
  if (!dobMoment || !dobMoment.isValid()) return false;
  const { minDobMoment, maxDobMoment } = getDobBounds();
  return (
    !dobMoment.isBefore(minDobMoment, "day") &&
    !dobMoment.isAfter(maxDobMoment, "day")
  );
};

const getDefaultProfile = () => ({
  abhaNumber: "",
  mrn: "",
  title: "Mr",
  name: "",
  dob: "",
  gender: "male",
  phoneCode: "91",
  phoneNumber: "",
  emergencyCode: "91",
  emergencyNumber: "",
  flatNo: "",
  address: "",
  city: "",
  district: "",
  state: "",
  pincode: "",
  country: "",
  coordinates: null,
});
const getDefaultCarePlan = () => ({
  serviceCategory: "",
  nurseSpecialist: "",
  physioModule: [],
  physioDurationType: "Days",
  physioDurationValue: "",
  physioFrequency: "",
  patientInstructions: "",
  nurseType: "",
  nurseRegularDutyKey: "",
  nurseRegularServiceKey: "",
  nurseOneTimeServices: [],
  nurseDays: "",
});

const normalizeGender = (value) =>
  String(value || "").toLowerCase() === "female" ? "female" : "male";

const splitPhoneParts = (raw, fallbackCode = "91") => {
  const digits = String(raw || "").replace(/\D/g, "");
  if (!digits) return { code: fallbackCode, number: "" };
  if (digits.length > 10) {
    return {
      code: digits.slice(0, digits.length - 10) || fallbackCode,
      number: digits.slice(-10),
    };
  }
  return { code: fallbackCode, number: digits.slice(0, 10) };
};

const mapPatientToProfile = (patient) => {
  const fallback = getDefaultProfile();
  if (!patient) return fallback;

  const pInfo = patient?.personalInfo || {};
  const basicInfo = pInfo?.basicInfo || {};
  const addrInfo =
    pInfo?.addressInfo || basicInfo?.addressInfo || patient?.addressInfo || {};
  const nameInfo = basicInfo?.nameInfo || {};
  const otherInfo = basicInfo?.otherInfo || {};

  const dobValue = otherInfo?.dob || basicInfo?.dob || patient?.dob || "";
  const parsedDob = dobValue ? new Date(dobValue) : null;
  const dobStr =
    parsedDob && !Number.isNaN(parsedDob.getTime())
      ? parsedDob.toISOString().split("T")[0]
      : "";

  const primaryPhone =
    otherInfo?.phone || basicInfo?.phone || patient?.phone || "";
  const emergencyPhone = patient?.emergencyContactInfo?.[0]?.phone || "";
  const primaryParts = splitPhoneParts(primaryPhone, "91");
  const emergencyParts = splitPhoneParts(
    emergencyPhone,
    primaryParts.code || "91",
  );

  return {
    abhaNumber: patient?.ABHA?.number || fallback.abhaNumber,
    mrn: patient?.mrn || patient?.patientID || fallback.mrn,
    title: nameInfo?.title || patient?.title || fallback.title,
    name:
      nameInfo?.fullName || nameInfo?.name || patient?.name || fallback.name,
    dob: dobStr || fallback.dob,
    gender: normalizeGender(
      otherInfo?.gender || basicInfo?.gender || patient?.gender,
    ),
    phoneCode: primaryParts.code || fallback.phoneCode,
    phoneNumber: primaryParts.number || fallback.phoneNumber,
    emergencyCode: emergencyParts.code || fallback.emergencyCode,
    emergencyNumber: emergencyParts.number || fallback.emergencyNumber,
    flatNo: addrInfo?.unitNumber || patient?.flatNo || fallback.flatNo,
    address:
      addrInfo?.address1 ||
      addrInfo?.area ||
      patient?.address ||
      fallback.address,
    city: addrInfo?.city || patient?.city || fallback.city,
    district: addrInfo?.district || patient?.district || fallback.district,
    state: addrInfo?.state || patient?.state || fallback.state,
    pincode: String(addrInfo?.pincode || patient?.pincode || ""),
    country: addrInfo?.country || patient?.country || "India",
    coordinates: addrInfo?.coordinates || fallback.coordinates,
  };
};

const PatientLocationPicker = ({
  isOpen,
  initialCoordinates,
  onClose,
  onConfirm,
}) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [selectedCoordinates, setSelectedCoordinates] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addressQuery, setAddressQuery] = useState("");
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);
  const [isReverseGeocoding, setIsReverseGeocoding] = useState(false);
  const [mapError, setMapError] = useState("");
  const [isLocating, setIsLocating] = useState(false);
  const [olaPublicBrowserKey, setOlaPublicBrowserKey] = useState("");

  useEffect(() => {
    if (!isOpen) return undefined;
    let isActive = true;

    getOlaPublicBrowserKey().then((publicBrowserKey) => {
      if (!isActive) return;
      setOlaPublicBrowserKey(publicBrowserKey);
      if (!publicBrowserKey) {
        setMapError(
          "Map configuration is missing on the server. Set OLA_API_KEY.",
        );
      }
    });

    return () => {
      isActive = false;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !mapContainerRef.current) return undefined;
    if (!olaPublicBrowserKey) return undefined;

    setMapError("");
    const startCoordinates = hasValidCoordinates(initialCoordinates)
      ? {
          lat: Number(initialCoordinates.lat),
          lng: Number(initialCoordinates.lng),
        }
      : DEFAULT_MAP_CENTER;
    setSelectedCoordinates(
      hasValidCoordinates(initialCoordinates) ? startCoordinates : null,
    );
    setSelectedAddress(null);
    setAddressQuery("");
    setAddressSuggestions([]);

    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style:
        "https://api.olamaps.io/tiles/vector/v1/styles/default-light-standard/style.json",
      center: [startCoordinates.lng, startCoordinates.lat],
      zoom: hasValidCoordinates(initialCoordinates) ? 16 : 11,
      transformRequest: (rawUrl, resourceType) => {
        try {
          const mapUrl = new URL(rawUrl);
          if (
            mapUrl.hostname === "api.olamaps.io" &&
            !mapUrl.searchParams.has("api_key")
          ) {
            mapUrl.searchParams.set("api_key", olaPublicBrowserKey);
          }
          return { url: mapUrl.toString(), resourceType };
        } catch (error) {
          return { url: rawUrl, resourceType };
        }
      },
    });

    const marker = new maplibregl.Marker({ color: "#3E57E8", draggable: true })
      .setLngLat([startCoordinates.lng, startCoordinates.lat])
      .addTo(map);

    const updateCoordinates = async (lngLat) => {
      const coordinates = toCoordinatePair(lngLat.lat, lngLat.lng);
      if (!coordinates) return;
      marker.setLngLat([coordinates.lng, coordinates.lat]);
      setSelectedCoordinates(coordinates);
      setIsReverseGeocoding(true);
      const addressDetails = await reverseGeocodeCoordinates(coordinates);
      setSelectedAddress(addressDetails);
      if (addressDetails?.address) setAddressQuery(addressDetails.address);
      setIsReverseGeocoding(false);
    };

    marker.on("dragend", () => {
      updateCoordinates(marker.getLngLat()).catch(() =>
        setIsReverseGeocoding(false),
      );
    });
    map.on("click", (event) => {
      updateCoordinates(event.lngLat).catch(() => setIsReverseGeocoding(false));
    });
    map.on("error", (event) => {
      const message = event?.error?.message || event?.message || "";
      if (message.includes("3d_model")) return;
      if (event?.error?.status === 403) {
        setMapError(
          "Map access was denied. Check the Ola browser-key restrictions.",
        );
      }
    });

    mapRef.current = map;
    markerRef.current = marker;

    return () => {
      marker.remove();
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
  }, [isOpen, initialCoordinates, olaPublicBrowserKey]);

  if (!isOpen) return null;

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      setMapError("Current location is not supported by this browser.");
      return;
    }

    setIsLocating(true);
    setMapError("");
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        const coordinates = toCoordinatePair(coords.latitude, coords.longitude);
        setIsLocating(false);
        if (!coordinates) {
          setMapError("The browser returned an invalid location.");
          return;
        }
        setSelectedCoordinates(coordinates);
        markerRef.current?.setLngLat([coordinates.lng, coordinates.lat]);
        mapRef.current?.flyTo({
          center: [coordinates.lng, coordinates.lat],
          zoom: 16,
          essential: true,
        });
        setIsReverseGeocoding(true);
        const addressDetails = await reverseGeocodeCoordinates(coordinates);
        setSelectedAddress(addressDetails);
        if (addressDetails?.address) setAddressQuery(addressDetails.address);
        setIsReverseGeocoding(false);
      },
      () => {
        setIsLocating(false);
        setMapError(
          "Unable to get the current location. Check browser permission and try again.",
        );
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  };

  const handleMapAddressSearch = async (event) => {
    const query = event.target.value;
    setAddressQuery(query);
    setSelectedAddress(null);
    if (query.trim().length < 3) {
      setAddressSuggestions([]);
      return;
    }

    setIsSearchingAddress(true);
    try {
      setAddressSuggestions(await searchPlaces(query));
    } finally {
      setIsSearchingAddress(false);
    }
  };

  const handleMapAddressSelection = async (suggestion) => {
    setAddressSuggestions([]);
    setAddressQuery(suggestion.description || "");
    setIsSearchingAddress(true);
    try {
      const details = await getPlaceDetails(suggestion.place_id);
      const coordinates = details?.geometry?.location
        ? toCoordinatePair(
            details.geometry.location.lat,
            details.geometry.location.lng,
          )
        : null;
      if (!coordinates) {
        setMapError("Coordinates were not available for this address.");
        return;
      }

      setMapError("");
      setSelectedCoordinates(coordinates);
      setSelectedAddress(mapAddressDetails(details, coordinates));
      markerRef.current?.setLngLat([coordinates.lng, coordinates.lat]);
      mapRef.current?.flyTo({
        center: [coordinates.lng, coordinates.lat],
        zoom: 16,
        essential: true,
      });
    } finally {
      setIsSearchingAddress(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center bg-black/50 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="flex h-[min(680px,90vh)] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <h3 className="font-poppins text-[16px] font-semibold text-gray-800">
              Pick patient location
            </h3>
            <p className="mt-1 font-poppins text-[11px] text-gray-500">
              Click the map or drag the pin to the patient's exact location.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full bg-gray-50 p-2 text-gray-500"
          >
            <MdClose size={20} />
          </button>
        </div>

        <div className="relative border-b border-gray-100 bg-white px-5 py-3">
          <label className="mb-1 block font-poppins text-[11px] font-medium text-gray-700">
            Search address
          </label>
          <input
            type="text"
            value={addressQuery}
            onChange={handleMapAddressSearch}
            className={inputClass}
            placeholder="Enter locality, landmark, street, or pincode..."
            autoComplete="off"
          />
          {isSearchingAddress ? (
            <div className="absolute right-8 top-11 h-4 w-4 animate-spin rounded-full border-2 border-[#3E57E8] border-t-transparent" />
          ) : null}
          {addressSuggestions.length > 0 ? (
            <div className="absolute left-5 right-5 top-[74px] z-20 max-h-48 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg">
              {addressSuggestions.map((suggestion) => (
                <button
                  key={suggestion.place_id}
                  type="button"
                  onClick={() => handleMapAddressSelection(suggestion)}
                  className="block w-full border-b border-gray-100 px-4 py-2.5 text-left font-poppins text-[12px] text-gray-700 last:border-b-0 hover:bg-gray-50"
                >
                  {suggestion.description}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div className="relative min-h-0 flex-1 bg-gray-100">
          <div ref={mapContainerRef} className="h-full w-full" />
          <button
            type="button"
            onClick={useCurrentLocation}
            disabled={isLocating || !olaPublicBrowserKey}
            className="absolute right-4 top-4 inline-flex items-center gap-2 rounded-lg bg-white px-3 py-2 font-poppins text-[12px] font-medium text-[#3E57E8] shadow-md disabled:cursor-not-allowed disabled:opacity-60"
          >
            <MdMyLocation size={16} />
            {isLocating ? "Locating..." : "Use current location"}
          </button>
          {mapError ? (
            <div className="absolute bottom-4 left-4 right-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 font-poppins text-[12px] text-red-600">
              {mapError}
            </div>
          ) : null}
          {isReverseGeocoding ? (
            <div className="absolute bottom-4 left-4 rounded-lg bg-white px-3 py-2 font-poppins text-[11px] text-gray-600 shadow-md">
              Finding address...
            </div>
          ) : null}
        </div>

        <div className="flex flex-col gap-3 border-t border-gray-100 bg-gray-50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="font-poppins text-[12px] text-gray-600">
            {hasValidCoordinates(selectedCoordinates)
              ? `${Number(selectedCoordinates.lat).toFixed(6)}, ${Number(selectedCoordinates.lng).toFixed(6)}`
              : "Select a valid location"}
          </p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-gray-200 bg-white px-5 py-2.5 font-poppins text-[12px] text-gray-600"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() =>
                onConfirm({
                  coordinates: selectedCoordinates,
                  addressDetails: selectedAddress,
                })
              }
              disabled={
                !hasValidCoordinates(selectedCoordinates) ||
                Boolean(mapError) ||
                isReverseGeocoding
              }
              className="rounded-xl bg-[#3E57E8] px-5 py-2.5 font-poppins text-[12px] font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              Confirm location
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function AddPatientModal({
  isOpen,
  onClose,
  onSave,
  onUploadDischargeSummary,
  mode = "add",
  initialPatient = null,
  existingDischargeSummaries = [],
  existingDischargeSummariesLoading = false,
  existingDischargeSummariesError = "",
}) {
  const [currentStep, setCurrentStep] = useState(1);
  const [entryMode, setEntryMode] = useState("single");
  const [createdPatientId, setCreatedPatientId] = useState("");
  const [isUploadingDischargeSummary, setIsUploadingDischargeSummary] =
    useState(false);
  const [isSubmittingCarePlan, setIsSubmittingCarePlan] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkParsing, setBulkParsing] = useState(false);
  const isAddMode = mode === "add";
  const isEditMode = mode === "edit";
  const isPrescribeMode = mode === "prescribe";
  const modalTitle = isEditMode
    ? "Edit Patient"
    : isPrescribeMode
      ? "Prescription"
      : "Add New Patient";

  const [profile, setProfile] = useState(getDefaultProfile());

  const [casedata, setCase] = useState([]);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);
  const [addressMode, setAddressMode] = useState("manual");
  const [isLocationPickerOpen, setIsLocationPickerOpen] = useState(false);
  const [isGoogleMapsLinkModalOpen, setIsGoogleMapsLinkModalOpen] =
    useState(false);
  const [googleMapsLink, setGoogleMapsLink] = useState("");
  const [googleMapsLinkError, setGoogleMapsLinkError] = useState("");
  const [isResolvingGoogleMapsLink, setIsResolvingGoogleMapsLink] =
    useState(false);
  const addressRef = useRef(null);
  const [errors, setErrors] = useState({});
  const [isCheckingPhone, setIsCheckingPhone] = useState(false);

  const [pdfTitle, setPdfTitle] = useState("");
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState("");
  const [pdfError, setPdfError] = useState("");
  const [selectedExistingSummary, setSelectedExistingSummary] = useState(null);
  const [selectedDischargeSummaryId, setSelectedDischargeSummaryId] =
    useState("");

  const [carePlan, setCarePlan] = useState(getDefaultCarePlan());
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkErrors, setBulkErrors] = useState([]);
  const [bulkValidatedRows, setBulkValidatedRows] = useState([]);
  const [bulkFileList, setBulkFileList] = useState([]);

  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingError, setPricingError] = useState("");
  // Server-provided cancellation policy disclosed to hospital staff before
  // they confirm a booking on the patient's behalf.
  const [cancellationPolicy, setCancellationPolicy] = useState(null);
  const [cancellationPolicyStatus, setCancellationPolicyStatus] =
    useState("loading");
  const [pricing, setPricing] = useState({
    partnerServices: [],
    nurseRegularSlots: [],
    nurseOneTimeServices: [],
    physioTreatments: [],
    physioAssessmentFee: 0,
  });
  const getProfileCompletionCountForPrescription = (patient) => {
    const checks = [
      patient?.medicalHistory?.completed === true,
      patient?.preExistingConditions?.completed === true,
      patient?.allergies?.completed === true,
      patient?.familyHistory?.completed === true,
      patient?.personalInfo?.completed === true,
    ];
    return checks.filter(Boolean).length;
  };
  const profileCompletionCountForPrescription = useMemo(
    () => getProfileCompletionCountForPrescription(initialPatient),
    [initialPatient],
  );
  const isPersonalInfoCompletedForPrescription =
    initialPatient?.personalInfo?.completed === true;
  const isClinicalProfileCompleteForPrescription =
    profileCompletionCountForPrescription === 5;
  const hasExistingDischargeSummaries = useMemo(
    () =>
      Array.isArray(existingDischargeSummaries) &&
      existingDischargeSummaries.length > 0,
    [existingDischargeSummaries],
  );
  const isDischargeProfileCompleteForPrescription =
    hasExistingDischargeSummaries && isPersonalInfoCompletedForPrescription;
  const isProfileCompleteForPrescription =
    isDischargeProfileCompleteForPrescription ||
    isClinicalProfileCompleteForPrescription;

  const resolveDischargeSummaryId = (summary) => {
    const raw = summary?._id || summary?.id || "";
    if (!raw) return "";
    if (typeof raw === "object" && raw?.$oid) return String(raw.$oid);
    return String(raw);
  };

  const getDischargeSummarySelectionKey = (summary) =>
    resolveDischargeSummaryId(summary) || String(summary?.key || "");

  const extractUploadedDischargeSummaryId = (uploadRes) => {
    const summaries = Array.isArray(uploadRes?.data?.dischargeSummaries)
      ? uploadRes.data.dischargeSummaries
      : [];
    if (!summaries.length) return "";
    const latest = summaries[summaries.length - 1];
    return resolveDischargeSummaryId(latest);
  };

  const clearExistingSummarySelection = () => {
    setSelectedExistingSummary(null);
    setSelectedDischargeSummaryId("");
  };

  const resetTransientUiState = () => {
    const nextState = getFreshTransientUiState();
    setErrors(nextState.errors);
    setAddressSuggestions(nextState.addressSuggestions);
    setIsCheckingPhone(nextState.isCheckingPhone);
    setAddressMode("manual");
    setIsLocationPickerOpen(false);
    setIsGoogleMapsLinkModalOpen(false);
    setGoogleMapsLink("");
    setGoogleMapsLinkError("");
    setIsResolvingGoogleMapsLink(false);
  };

  const handleExistingSummarySelection = (summary) => {
    const clickedKey = getDischargeSummarySelectionKey(summary);
    const currentKey = getDischargeSummarySelectionKey(selectedExistingSummary);
    const shouldClear = clickedKey && currentKey && clickedKey === currentKey;
    if (shouldClear) {
      clearExistingSummarySelection();
      return;
    }
    setSelectedExistingSummary(summary);
    setSelectedDischargeSummaryId(resolveDischargeSummaryId(summary));
  };

  useEffect(() => {
    if (isOpen) {
      const presetPatientId = resolvePatientRecordId(initialPatient);
      resetTransientUiState();

      setCurrentStep(isPrescribeMode ? 2 : 1);
      setEntryMode("single");
      setCreatedPatientId(
        isPrescribeMode || isEditMode ? String(presetPatientId || "") : "",
      );
      setProfile(
        isEditMode ? mapPatientToProfile(initialPatient) : getDefaultProfile(),
      );
      resetBulkState();

      setPdfTitle("");
      setPdfFile(null);
      setPdfPreviewUrl("");
      setPdfError("");
      setSelectedExistingSummary(null);
      setSelectedDischargeSummaryId("");

      setCarePlan(getDefaultCarePlan());
      setCancellationPolicy(null);
      setCancellationPolicyStatus("loading");

      loadLookupData();
      fetchPartnerPricing();
      loadCancellationPolicy();
    }
  }, [isOpen, isPrescribeMode, isEditMode, initialPatient]);

  const loadCancellationPolicy = async () => {
    setCancellationPolicyStatus("loading");
    const response = await getCancellationPolicyDisclosure();
    if (response?.error === false && response?.data?.enabled) {
      setCancellationPolicy(response.data.policy);
      setCancellationPolicyStatus("ready");
    } else if (response?.error === false && response?.data?.enabled === false) {
      setCancellationPolicy(null);
      setCancellationPolicyStatus("not_required");
    } else {
      setCancellationPolicy(null);
      setCancellationPolicyStatus("error");
    }
  };

  useEffect(() => {
    if (!isOpen || !isPrescribeMode) return;
    if (
      !Array.isArray(existingDischargeSummaries) ||
      existingDischargeSummaries.length === 0
    ) {
      setSelectedExistingSummary(null);
      return;
    }
    setSelectedExistingSummary((prev) => {
      const prevKey = getDischargeSummarySelectionKey(prev);
      if (!prevKey) return null;
      const stillExists = existingDischargeSummaries.find(
        (item) => getDischargeSummarySelectionKey(item) === prevKey,
      );
      return stillExists || null;
    });
  }, [isOpen, isPrescribeMode, existingDischargeSummaries]);

  useEffect(() => {
    if (!isOpen || !isEditMode) return;
    const patientId = resolvePatientRecordId(initialPatient);
    if (!patientId) return;

    let cancelled = false;
    (async () => {
      try {
        const freshRes = await getPatientById(patientId);
        if (!cancelled && freshRes?.error === false) {
          setProfile(mapPatientToProfile(freshRes?.data));
        }
      } catch (error) {
        console.error("Error fetching patient details for edit:", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, isEditMode, initialPatient]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (addressRef.current && !addressRef.current.contains(e.target)) {
        setAddressSuggestions([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadLookupData = async () => {
    try {
      const result = await getLookupData();
      if (result?.error === false) {
        setCase(Array.isArray(result?.data) ? result.data : []);
      }
    } catch (error) {
      console.error("Error fetching lookup data:", error);
    }
  };

  const fetchPartnerPricing = async () => {
    try {
      setPricingLoading(true);
      setPricingError("");

      const res = await getPartner();
      if (!res || res?.error) {
        setPricingError(res?.message || "Failed to fetch partner pricing");
        return;
      }

      const data = res?.data || {};
      const partnerServices = safeArr(data?.services)
        .map((item) => extractPartnerServiceString(item))
        .filter(Boolean);

      const nurseRegularSlots = safeArr(
        data?.pricingInfo?.nurseService?.regularShifts?.slots,
      )
        .map((x, index) => {
          const mappedServices = safeArr(x?.services)
            .map((item) => {
              if (typeof item === "string") return item.trim();
              return String(
                item?.service ||
                  item?.name ||
                  item?.label ||
                  item?.serviceType ||
                  "",
              ).trim();
            })
            .filter(Boolean);
          const serviceName = String(
            x?.serviceName || x?.name || x?.service || "",
          ).trim();
          const role = normalizeRegularRole(x?.role || x?.staffRole || "nurse");
          return {
            shift: x?.shift,
            type: x?.type,
            serviceName,
            role,
            fee: resolvePartnerFee(x?.fee ?? x?.fees),
            services: mappedServices,
            notes: String(x?.notes || "").trim(),
            foodAccommodation: safeArr(x?.foodAccommodation),
            travelAllowance: safeArr(x?.travelAllowance),
            slotKey: regularSlotKey(x?.shift, x?.type, serviceName, index),
          };
        })
        .filter((x) => x.shift && x.type && x.fee > 0 && x.serviceName);

      const nurseOneTimeServices = safeArr(
        data?.pricingInfo?.nurseService?.oneTimeShifts?.services,
      )
        .map((x) => ({
          serviceType: x?.serviceType,
          fee: resolvePartnerFee(x?.fee ?? x?.fees),
          notes: String(x?.notes || "").trim(),
        }))
        .filter((x) => x.serviceType && x.fee > 0);

      const physioNode =
        data?.pricingInfo?.physioService ||
        data?.pricingInfo?.physioServices ||
        data?.pricingInfo?.physio_service ||
        {};

      const physioAssessmentFee = toNumber(physioNode?.assessmentFees);

      const physioTreatments = safeArr(physioNode?.services)
        .map((x) => ({
          serviceType: x?.name || x?.serviceType || x?.label || "",
          fee: resolvePartnerFee(x?.fee ?? x?.fees),
          notes: String(x?.notes || "").trim(),
        }))
        .filter((x) => x.serviceType && x.fee > 0);

      setPricing({
        partnerServices,
        nurseRegularSlots,
        nurseOneTimeServices,
        physioTreatments,
        physioAssessmentFee,
      });
    } catch (e) {
      console.error("fetchPartnerPricing error:", e);
      setPricingError("Failed to fetch partner pricing");
    } finally {
      setPricingLoading(false);
    }
  };

  const titleOptions = Array.isArray(casedata)
    ? casedata.filter(
        (item) => item.type === "title" && item.label !== "Master.",
      )
    : [];

  const phoneCodeOptions = Array.isArray(casedata)
    ? casedata.filter((item) => item.type === "phonecode")
    : [];

  const normalizePhoneCodeValue = (raw) => {
    const val = String(raw ?? "").trim();
    if (!val) return "91";
    return val.replace(/^\+/, "");
  };

  const getPhoneCodeLabel = (item) => {
    const label = String(item?.label ?? "").trim();
    if (label) return label;
    const value = normalizePhoneCodeValue(item?.value);
    return `+${value}`;
  };

  useEffect(() => {
    if (!isOpen) return;
    const titleOpts = Array.isArray(casedata)
      ? casedata.filter(
          (item) => item.type === "title" && item.label !== "Master.",
        )
      : [];
    const phoneOpts = Array.isArray(casedata)
      ? casedata.filter((item) => item.type === "phonecode")
      : [];
    if (!titleOpts.length && !phoneOpts.length) return;

    const allowedTitleValues = new Set(
      titleOpts.map((item) => String(item?.value ?? "").trim()).filter(Boolean),
    );
    const allowedDialCodes = new Set(
      phoneOpts.map((item) => normalizePhoneCodeValue(item?.value)),
    );
    const fallbackTitle = titleOpts[0]?.value || "Mr";
    const fallbackCode = normalizePhoneCodeValue(phoneOpts[0]?.value || "91");

    setProfile((prev) => ({
      ...prev,
      title:
        allowedTitleValues.size &&
        !allowedTitleValues.has(String(prev.title || "").trim())
          ? fallbackTitle
          : prev.title,
      phoneCode:
        allowedDialCodes.size && !allowedDialCodes.has(prev.phoneCode)
          ? fallbackCode
          : prev.phoneCode,
      emergencyCode:
        allowedDialCodes.size && !allowedDialCodes.has(prev.emergencyCode)
          ? fallbackCode
          : prev.emergencyCode,
    }));
  }, [isOpen, casedata]);

  const filteredRegularSlotsByRole = useMemo(() => {
    const selectedRole = normalizeRegularRole(carePlan.nurseSpecialist);
    if (!carePlan.nurseSpecialist) return [];
    return (pricing.nurseRegularSlots || []).filter(
      (slot) => normalizeRegularRole(slot?.role) === selectedRole,
    );
  }, [pricing.nurseRegularSlots, carePlan.nurseSpecialist]);

  const nurseRegularDutyOptions = useMemo(() => {
    const seen = new Set();
    return filteredRegularSlotsByRole
      .filter((slot) => slot?.shift && slot?.type)
      .filter((slot) => {
        const key = dutyKey(slot.shift, slot.type);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((x) => ({
        value: dutyKey(x.shift, x.type),
        label: `${x.type} Duty - ${shiftLabel(x.shift)}`,
        shift: x.shift,
        type: x.type,
      }));
  }, [filteredRegularSlotsByRole]);

  const selectedRegularDuty = useMemo(() => {
    if (!carePlan.nurseRegularDutyKey) return null;
    return (
      (nurseRegularDutyOptions || []).find(
        (opt) => opt.value === carePlan.nurseRegularDutyKey,
      ) || null
    );
  }, [carePlan.nurseRegularDutyKey, nurseRegularDutyOptions]);

  const nurseRegularServiceOptions = useMemo(() => {
    if (!carePlan.nurseRegularDutyKey) return [];
    return filteredRegularSlotsByRole
      .filter(
        (slot) =>
          dutyKey(slot.shift, slot.type) === carePlan.nurseRegularDutyKey,
      )
      .map((slot) => ({
        value: slot.slotKey,
        label: slot.serviceName,
        notes: slot.notes || "",
        services: safeArr(slot.services),
      }));
  }, [filteredRegularSlotsByRole, carePlan.nurseRegularDutyKey]);

  const selectedRegularSlot = useMemo(() => {
    if (!carePlan.nurseRegularServiceKey) return null;
    return (
      filteredRegularSlotsByRole.find(
        (slot) => slot.slotKey === carePlan.nurseRegularServiceKey,
      ) || null
    );
  }, [filteredRegularSlotsByRole, carePlan.nurseRegularServiceKey]);

  const nurseOneTimeServiceOptions = useMemo(() => {
    return (pricing.nurseOneTimeServices || []).map((x) => ({
      value: x.serviceType,
      label: x.serviceType,
      fee: x.fee,
      notes: x.notes || "",
    }));
  }, [pricing.nurseOneTimeServices]);

  const physioModuleOptions = useMemo(() => {
    return (pricing.physioTreatments || []).map((x) => ({
      value: x.serviceType,
      label: x.serviceType,
      fee: x.fee,
      notes: x.notes || "",
    }));
  }, [pricing.physioTreatments]);

  const serviceCategoryOptions = useMemo(() => {
    const fromPartnerServices = mapPartnerServicesToCareCategories(
      pricing?.partnerServices,
    );
    if (fromPartnerServices.length) return fromPartnerServices;

    const inferred = [];
    if (
      (pricing?.nurseRegularSlots || []).length ||
      (pricing?.nurseOneTimeServices || []).length
    ) {
      inferred.push({ value: "Nursing Care", label: "Nursing Care" });
    }
    if ((pricing?.physioTreatments || []).length) {
      inferred.push({ value: "Physio", label: "Physiotherapy" });
    }
    return inferred;
  }, [
    pricing?.partnerServices,
    pricing?.nurseRegularSlots,
    pricing?.nurseOneTimeServices,
    pricing?.physioTreatments,
  ]);

  const physioFrequencyOptions = useMemo(() => {
    const allOptions = [
      { value: "Every day", label: "Daily" },
      { value: "Alternative days", label: "Alternative Days" },
      { value: "Twice in week", label: "Twice in a Week" },
      { value: "Thrice in week", label: "Thrice in a Week" },
    ];

    const durationValue = Number(carePlan.physioDurationValue || 0);
    if (!Number.isFinite(durationValue) || durationValue <= 0)
      return allOptions;

    const durationType = String(carePlan.physioDurationType || "Days");
    let totalDays = durationValue;
    if (durationType === "Weeks") totalDays = durationValue * 7;
    if (durationType === "Months") totalDays = durationValue * 30;

    if (totalDays > 5) return allOptions;
    if (totalDays >= 3) return allOptions.slice(0, 3);
    return allOptions.slice(0, 2);
  }, [carePlan.physioDurationType, carePlan.physioDurationValue]);

  useEffect(() => {
    if (carePlan.serviceCategory !== "Physio") return;
    if (!carePlan.physioFrequency) return;
    const exists = physioFrequencyOptions.some(
      (opt) => opt.value === carePlan.physioFrequency,
    );
    if (!exists) {
      setCarePlan((prev) => ({ ...prev, physioFrequency: "" }));
    }
  }, [
    carePlan.serviceCategory,
    carePlan.physioFrequency,
    physioFrequencyOptions,
  ]);

  useEffect(() => {
    if (!carePlan.serviceCategory) return;
    const stillSupported = serviceCategoryOptions.some(
      (option) => option.value === carePlan.serviceCategory,
    );
    if (stillSupported) return;

    setCarePlan((prev) => ({
      ...prev,
      serviceCategory: "",
      nurseSpecialist: "",
      physioModule: [],
      physioDurationType: "Days",
      physioDurationValue: "",
      physioFrequency: "",
      nurseType: "",
      nurseRegularDutyKey: "",
      nurseRegularServiceKey: "",
      nurseOneTimeServices: [],
      nurseDays: "",
    }));
  }, [carePlan.serviceCategory, serviceCategoryOptions]);

  if (!isOpen) return null;

  const handleProfileChange = (e) => {
    let { name, value } = e.target;
    if (name === "phoneNumber" || name === "emergencyNumber") {
      const onlyDigits = value.replace(/\D/g, "");
      value = onlyDigits.length > 10 ? onlyDigits.slice(-10) : onlyDigits;
    }

    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    if (name === "phoneCode" || name === "phoneNumber") {
      setErrors((prev) => {
        if (!prev.phoneNumber) return prev;
        const newErrors = { ...prev };
        delete newErrors.phoneNumber;
        return newErrors;
      });
    }

    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const onAddressChange = async (e) => {
    const query = e.target.value;

    if (errors.address) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.address;
        return newErrors;
      });
    }

    setProfile((prev) => ({
      ...prev,
      address: query,
      city: "",
      district: "",
      state: "",
      pincode: "",
      coordinates: null,
    }));
    setGoogleMapsLinkError("");

    if (query.length >= 3) {
      setIsSearchingAddress(true);
      try {
        const results = await searchPlaces(query);
        setAddressSuggestions(results);
      } catch (err) {
        setAddressSuggestions([]);
      } finally {
        setIsSearchingAddress(false);
      }
    } else {
      setAddressSuggestions([]);
    }
  };

  const handleSelectAddressSuggestion = async (suggestion) => {
    setAddressSuggestions([]);
    setProfile((prev) => ({ ...prev, address: suggestion.description }));

    try {
      const details = await getPlaceDetails(suggestion.place_id);
      if (details) {
        const components = details.address_components || [];
        let city = "";
        let district = "";
        let state = "";
        let country = "";
        let pincode = "";

        components.forEach((c) => {
          if (c.types.includes("locality") || c.types.includes("sublocality"))
            city = c.short_name;
          if (c.types.includes("administrative_area_level_2"))
            district = c.short_name;
          if (c.types.includes("administrative_area_level_1"))
            state = c.short_name;
          if (c.types.includes("country")) country = c.short_name;
          if (c.types.includes("postal_code")) pincode = c.short_name;
        });

        setProfile((prev) => ({
          ...prev,
          address: suggestion.description,
          city,
          district: district || city,
          state,
          country: country || "India",
          pincode,
          coordinates: details.geometry?.location
            ? {
                lat: details.geometry.location.lat,
                lng: details.geometry.location.lng,
              }
            : null,
        }));
      }
    } catch (error) {
      console.error("Error fetching Ola details:", error);
    }
  };

  const applyAddressCoordinates = (coordinates, addressDetails = null) => {
    if (!hasValidCoordinates(coordinates)) return;
    setProfile((prev) => ({
      ...prev,
      ...(addressDetails?.address ? { address: addressDetails.address } : {}),
      ...(addressDetails?.city ? { city: addressDetails.city } : {}),
      ...(addressDetails?.district
        ? { district: addressDetails.district }
        : {}),
      ...(addressDetails?.state ? { state: addressDetails.state } : {}),
      ...(addressDetails?.country ? { country: addressDetails.country } : {}),
      ...(addressDetails?.pincode ? { pincode: addressDetails.pincode } : {}),
      coordinates: {
        lat: Number(coordinates.lat),
        lng: Number(coordinates.lng),
      },
    }));
    setErrors((prev) => {
      if (!prev.address) return prev;
      const nextErrors = { ...prev };
      delete nextErrors.address;
      return nextErrors;
    });
  };

  const handleMapLocationSelected = ({ coordinates, addressDetails }) => {
    applyAddressCoordinates(coordinates, addressDetails);
    setIsLocationPickerOpen(false);
    setGoogleMapsLinkError("");
  };

  const handleApplyGoogleMapsLink = async () => {
    const result = parseGoogleMapsCoordinates(googleMapsLink);
    if (result.error) {
      setGoogleMapsLinkError(result.error);
      return;
    }

    setIsResolvingGoogleMapsLink(true);
    try {
      const addressDetails = await reverseGeocodeCoordinates(
        result.coordinates,
      );
      applyAddressCoordinates(result.coordinates, addressDetails);
      setGoogleMapsLinkError("");
      setIsGoogleMapsLinkModalOpen(false);
    } finally {
      setIsResolvingGoogleMapsLink(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type === "application/pdf") {
      if (file.size > MAX_PDF_SIZE_BYTES) {
        setPdfFile(null);
        setPdfPreviewUrl("");
        setPdfError("PDF size must be 10MB or less.");
        alert("PDF size must be 10MB or less.");
        return;
      }
      setPdfError("");
      setPdfFile(file);
      setPdfPreviewUrl(URL.createObjectURL(file));
    } else {
      setPdfError("Please upload a valid PDF file.");
      alert("Please upload a valid PDF file.");
    }
  };

  const handleCarePlanChange = (e) => {
    const { name, value } = e.target;

    if (name === "serviceCategory") {
      setCarePlan((prev) => ({
        ...prev,
        serviceCategory: value,
        nurseSpecialist: "",
        physioModule: [],
        physioDurationType: "Days",
        physioDurationValue: "",
        physioFrequency: "",
        nurseType: "",
        nurseRegularDutyKey: "",
        nurseRegularServiceKey: "",
        nurseOneTimeServices: [],
        nurseDays: "",
      }));
      return;
    }

    if (name === "physioDurationValue" || name === "nurseDays") {
      const onlyDigits = String(value || "")
        .replace(/\D/g, "")
        .slice(0, 2);
      setCarePlan((prev) => ({ ...prev, [name]: onlyDigits }));
      return;
    }

    if (name === "nurseType") {
      setCarePlan((prev) => ({
        ...prev,
        nurseType: value,
        nurseRegularDutyKey: "",
        nurseRegularServiceKey: "",
        nurseOneTimeServices: [],
        nurseDays: "",
      }));
      return;
    }

    if (name === "nurseSpecialist") {
      setCarePlan((prev) => ({
        ...prev,
        nurseSpecialist: value,
        nurseRegularDutyKey: "",
        nurseRegularServiceKey: "",
      }));
      return;
    }

    if (name === "nurseRegularDutyKey") {
      setCarePlan((prev) => ({
        ...prev,
        nurseRegularDutyKey: value,
        nurseRegularServiceKey: "",
      }));
      return;
    }

    setCarePlan((prev) => ({ ...prev, [name]: value }));
  };

  const resetBulkState = () => {
    setBulkRows([]);
    setBulkErrors([]);
    setBulkValidatedRows([]);
    setBulkFileList([]);
    setBulkSubmitting(false);
    setBulkParsing(false);
  };

  const validateBulkHeaders = (headers) => {
    const normalized = (headers || []).map(normalizeBulkHeader);
    return BULK_REQUIRED_HEADERS.filter(
      (header) => !normalized.includes(header),
    );
  };

  const validateBulkRows = (rows) => {
    const errors = [];
    const mapped = [];
    const seenPhones = new Map();

    (rows || []).forEach((row, index) => {
      const rowNo = index + 2;
      const cleanRow = {};
      Object.keys(row || {}).forEach((key) => {
        cleanRow[normalizeBulkHeader(key)] = row[key];
      });

      const mrn = String(cleanRow.mrn || "").trim();
      const title = normalizeBulkTitle(cleanRow.title);
      const name = normalizeBulkName(cleanRow.name);
      const dobMoment = parseBulkDob(cleanRow.dob);
      const gender = normalizeBulkGender(cleanRow.gender);
      const phone = normalizeBulkPhone(cleanRow.phone);

      if (!mrn) errors.push(`Row ${rowNo}: mrn is required`);
      if (!title) {
        errors.push(
          `Row ${rowNo}: title must be one of ${BULK_ALLOWED_TITLES.join(", ")}`,
        );
      }
      if (!name) errors.push(`Row ${rowNo}: name is required`);
      if (name && !isValidName(name)) {
        errors.push(
          `Row ${rowNo}: name must contain only letters/spaces and be 2-50 characters`,
        );
      }
      if (!dobMoment) {
        errors.push(`Row ${rowNo}: dob must be in DD/MM/YYYY format`);
      } else if (!isBulkDobInRange(dobMoment)) {
        errors.push(`Row ${rowNo}: dob is out of allowed range`);
      }
      if (!gender) {
        errors.push(
          `Row ${rowNo}: gender must be one of ${BULK_ALLOWED_GENDERS.join(", ")}`,
        );
      }
      if (!phone) {
        errors.push(`Row ${rowNo}: phone is required`);
      } else if (!isValidBulkPhone(phone)) {
        errors.push(`Row ${rowNo}: phone must be in 91XXXXXXXXXX format`);
      }
      if (
        !mrn ||
        !title ||
        !name ||
        !dobMoment ||
        !gender ||
        !phone ||
        !isValidBulkPhone(phone)
      ) {
        return;
      }

      if (seenPhones.has(phone)) {
        errors.push(
          `Row ${rowNo}: duplicate phone "${phone}" (already in row ${seenPhones.get(phone)})`,
        );
        return;
      }
      seenPhones.set(phone, rowNo);

      mapped.push({
        mrn,
        title,
        name,
        dob: dobMoment.format("DD/MM/YYYY"),
        gender,
        phone,
      });
    });

    return { errors, rows: mapped };
  };

  const afterBulkParse = (dataRows, file) => {
    setBulkRows(dataRows || []);
    setBulkFileList(file ? [file] : []);

    const { errors, rows } = validateBulkRows(dataRows || []);
    setBulkErrors(errors);
    setBulkValidatedRows(rows);

    if ((dataRows || []).length === 0) {
      message.error("Uploaded file is empty.");
      return;
    }
    if (errors.length > 0) {
      message.error(
        `File has ${errors.length} validation error(s). Fix and re-upload.`,
      );
      return;
    }
    message.success(
      `${file?.name || "File"} parsed successfully. ${rows.length} row(s) ready.`,
    );
  };

  const handleBulkFile = (file) => {
    const ext = String(file?.name || "")
      .split(".")
      .pop()
      .toLowerCase();
    const isCsv = ext === "csv";
    const isExcel = ext === "xlsx" || ext === "xls";
    if (!isCsv && !isExcel) {
      message.error("You can only upload CSV or Excel (.xlsx/.xls) files.");
      resetBulkState();
      return Upload.LIST_IGNORE;
    }

    setBulkParsing(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        if (isCsv) {
          Papa.parse(e.target.result, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
              setBulkParsing(false);
              if (results?.errors?.length > 0) {
                message.error("Error parsing CSV file.");
                resetBulkState();
                return;
              }
              const headers = results?.meta?.fields || [];
              const missingHeaders = validateBulkHeaders(headers);
              if (missingHeaders.length > 0) {
                setBulkRows([]);
                setBulkValidatedRows([]);
                setBulkErrors([
                  `Missing headers: ${missingHeaders.join(", ")}`,
                ]);
                setBulkFileList([file]);
                message.error(`Missing headers: ${missingHeaders.join(", ")}`);
                return;
              }
              afterBulkParse(results?.data || [], file);
            },
            error: () => {
              setBulkParsing(false);
              message.error("Error parsing CSV file.");
              resetBulkState();
            },
          });
          return;
        }

        const workbook = XLSX.read(new Uint8Array(e.target.result), {
          type: "array",
          cellDates: true,
        });
        const firstSheet = workbook?.SheetNames?.[0];
        const worksheet = workbook?.Sheets?.[firstSheet];
        const jsonRows = XLSX.utils.sheet_to_json(worksheet, {
          defval: "",
          raw: true,
        });
        const headers = jsonRows?.[0] ? Object.keys(jsonRows[0]) : [];
        const missingHeaders = validateBulkHeaders(headers);
        setBulkParsing(false);
        if (missingHeaders.length > 0) {
          setBulkRows([]);
          setBulkValidatedRows([]);
          setBulkErrors([`Missing headers: ${missingHeaders.join(", ")}`]);
          setBulkFileList([file]);
          message.error(`Missing headers: ${missingHeaders.join(", ")}`);
          return;
        }
        afterBulkParse(jsonRows || [], file);
      } catch (error) {
        setBulkParsing(false);
        message.error("Error reading file.");
        resetBulkState();
      }
    };
    reader.onerror = () => {
      setBulkParsing(false);
      message.error("Error reading file.");
      resetBulkState();
    };
    if (isCsv) reader.readAsText(file);
    else reader.readAsArrayBuffer(file);
    return false;
  };

  const downloadBulkSampleExcel = async () => {
    try {
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet("Patient Bulk Template");
      ws.columns = [
        { header: "mrn", key: "mrn", width: 20 },
        { header: "title", key: "title", width: 12 },
        { header: "name", key: "name", width: 30 },
        { header: "dob", key: "dob", width: 14 },
        { header: "gender", key: "gender", width: 12 },
        { header: "phone", key: "phone", width: 16 },
      ];
      ws.getRow(1).font = { bold: true };
      ws.getRow(1).alignment = { vertical: "middle", horizontal: "center" };
      ws.addRow({
        mrn: "MRN001",
        title: "Mr",
        name: "Arun Kumar",
        dob: "15/06/1990",
        gender: "male",
        phone: "919876543210",
      });

      ws.dataValidations.add("B2:B500", {
        type: "list",
        allowBlank: false,
        formulae: [`"${BULK_ALLOWED_TITLES.join(",")}"`],
      });
      ws.dataValidations.add("E2:E500", {
        type: "list",
        allowBlank: false,
        formulae: [`"${BULK_ALLOWED_GENDERS.join(",")}"`],
      });

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "Patient_Bulk_Upload_Template.xlsx");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      message.success("Sample template downloaded successfully.");
    } catch (error) {
      message.error("Failed to download sample template.");
    }
  };

  const handleBulkSubmit = async () => {
    if (!onSave) return;
    if (bulkRows.length === 0 || bulkFileList.length === 0) {
      showToast("Please upload a file.", "Error");
      return;
    }

    const { errors, rows } = validateBulkRows(bulkRows);
    if (errors.length > 0) {
      setBulkErrors(errors);
      setBulkValidatedRows(rows);
      message.error(
        `Cannot upload. Fix ${errors.length} error(s) and re-upload.`,
      );
      return;
    }

    const payload = {
      type: "bulk",
      data: rows.map((row) => ({
        mrn: row.mrn,
        phone: Number(row.phone),
        personalInfo: {
          completed: true,
          basicInfo: {
            nameInfo: { title: row.title, fullName: row.name },
            otherInfo: {
              phone: Number(row.phone),
              gender: row.gender,
              dob: row.dob,
            },
          },
        },
      })),
    };

    setBulkSubmitting(true);
    try {
      const res = await onSave(payload, {
        suppressSuccessToast: true,
        suppressErrorToast: true,
      });
      if (res && res.error === false) {
        message.success(res?.message || "Patients uploaded successfully.");
        resetBulkState();
        setEntryMode("single");
        onClose();
        return;
      }

      const backendErrors = Array.isArray(res?.data?.errors)
        ? res.data.errors
        : [];
      if (backendErrors.length > 0) {
        setBulkErrors(backendErrors);
      }
      message.error(res?.message || "Bulk upload failed.");
    } catch (error) {
      message.error("Bulk upload failed.");
    } finally {
      setBulkSubmitting(false);
    }
  };

  const handleNurseOneTimeServicesMulti = (selected) => {
    const values = selected ? selected.map((x) => x.value) : [];
    setCarePlan((prev) => ({ ...prev, nurseOneTimeServices: values }));
  };

  const handleNurseRegularServiceChange = (selected) => {
    setCarePlan((prev) => ({
      ...prev,
      nurseRegularServiceKey: String(selected?.value || ""),
    }));
  };
  const handleClearNurseRegularService = () => {
    setCarePlan((prev) => ({
      ...prev,
      nurseRegularServiceKey: "",
    }));
  };

  const handlePhysioServicesMulti = (selected) => {
    const values = selected ? selected.map((x) => x.value) : [];
    setCarePlan((prev) => ({ ...prev, physioModule: values }));
  };

  const validateStep1 = ({ skipPhoneValidation = false } = {}) => {
    const newErrors = {};
    const trimmedName = String(profile.name || "").trim();
    if (!trimmedName) {
      newErrors.name = "Required";
    } else if (!isValidName(trimmedName)) {
      newErrors.name = "Name must be 2-50 letters and spaces only";
    }

    const dobValue = String(profile.dob || "").trim();
    if (!dobValue) {
      newErrors.dob = "Required";
    } else {
      const parsedDob = moment(dobValue, "YYYY-MM-DD", true);
      const { minDobMoment, maxDobMoment } = getDobBounds();
      if (!parsedDob.isValid()) {
        newErrors.dob = "Invalid DOB";
      } else if (parsedDob.isAfter(maxDobMoment, "day")) {
        newErrors.dob = "Invalid DOB";
      } else if (parsedDob.isBefore(minDobMoment, "day")) {
        newErrors.dob = "Invalid DOB";
      }
    }

    if (
      !skipPhoneValidation &&
      (!profile.phoneNumber || profile.phoneNumber.length < 10)
    ) {
      newErrors.phoneNumber = "Valid 10-digit number required";
    }

    const trimmedAddress = String(profile.address || "").trim();
    if (!trimmedAddress) {
      newErrors.address = "Required";
    } else if (!hasValidCoordinates(profile.coordinates)) {
      newErrors.address =
        "Select an address suggestion, pick the location on the map, or add a Google Maps link";
    }

    return newErrors;
  };

  const resolvePatientRecordId = (data) =>
    data?._id || data?.id || data?.userID || data?.patientID || "";

  const buildPayload = () => {
    const cleanedPhoneCode = String(profile.phoneCode || "").replace("+", "");
    const fullPhone = Number(cleanedPhoneCode + profile.phoneNumber);

    const fullEmergencyPhone = profile.emergencyNumber
      ? Number(
          String(profile.emergencyCode || "").replace("+", "") +
            profile.emergencyNumber,
        )
      : null;

    return {
      ABHA: {
        number: profile.abhaNumber,
        isConnected: false,
        ...(profile.abhaNumber ? { lastUpdated: new Date() } : {}),
      },
      mrn: profile.mrn ? String(profile.mrn).trim() : "",
      phone: fullPhone,
      emergencyContactInfo: fullEmergencyPhone
        ? [{ name: profile.name, phone: fullEmergencyPhone }]
        : [],
      personalInfo: {
        completed: true,
        basicInfo: {
          nameInfo: { title: profile.title, fullName: profile.name },
          otherInfo: {
            phone: fullPhone,
            gender: profile.gender,
            dob: new Date(profile.dob),
          },
        },
        addressInfo: {
          unitNumber: profile.flatNo,
          address1: profile.address,
          address2: "",
          city: profile.city || "",
          district: profile.district || "",
          state: profile.state || "",
          pincode: profile.pincode ? Number(profile.pincode) : null,
          country: profile.country || "India",
          landmark: "",
          ...(profile.coordinates ? { coordinates: profile.coordinates } : {}),
        },
      },
    };
  };

  const buildUpdatePayload = (patientId) => {
    const fullPhone = Number(
      String(profile.phoneCode || "").replace("+", "") + profile.phoneNumber,
    );
    const fullEmergencyPhone = profile.emergencyNumber
      ? Number(
          String(profile.emergencyCode || "").replace("+", "") +
            profile.emergencyNumber,
        )
      : null;
    const parsedDob = profile.dob ? new Date(profile.dob) : null;
    const hasValidDob = parsedDob && !Number.isNaN(parsedDob.getTime());
    const otherInfo = {
      phone: fullPhone,
      gender: profile.gender,
      ...(hasValidDob ? { dob: parsedDob } : {}),
    };

    return {
      _id: patientId,
      mrn: profile.mrn ? String(profile.mrn).trim() : "",
      phone: fullPhone,
      personalInfo: {
        completed: true,
        basicInfo: {
          nameInfo: { title: profile.title, fullName: profile.name },
          otherInfo,
        },
        addressInfo: {
          unitNumber: profile.flatNo,
          address1: profile.address,
          city: profile.city || "",
          district: profile.district || "",
          state: profile.state || "",
          pincode: profile.pincode ? Number(profile.pincode) : null,
          country: profile.country || "India",
          ...(profile.coordinates ? { coordinates: profile.coordinates } : {}),
        },
      },
      contactPersonInfo: {
        completed: true,
        basicInfo: {
          nameInfo: { title: profile.title, fullName: profile.name },
          otherInfo,
        },
      },
      emergencyContactInfo: fullEmergencyPhone
        ? [{ name: profile.name, phone: fullEmergencyPhone }]
        : [],
    };
  };

  const validatePhoneUniqueness = async () => {
    const editablePatientId = String(
      createdPatientId || resolvePatientRecordId(initialPatient) || "",
    );
    const shouldSkipCheck =
      (!isAddMode && !isEditMode) || (isAddMode && createdPatientId);
    if (shouldSkipCheck) return true;

    const dialCode = String(profile.phoneCode || "").replace("+", "");
    const localNumber = String(profile.phoneNumber || "").trim();
    if (!dialCode || localNumber.length < 10) return true;

    const fullPhone = Number(`${dialCode}${localNumber}`);
    if (!Number.isFinite(fullPhone)) return true;

    setIsCheckingPhone(true);
    try {
      const checkRes = await checkPhoneExists(
        fullPhone,
        isEditMode ? editablePatientId : "",
      );
      if (!checkRes || typeof checkRes !== "object") {
        alert("Unable to verify phone number now. Please try again.");
        return false;
      }
      if (checkRes?.error === false && checkRes?.data?.allow === false) {
        setErrors((prev) => ({
          ...prev,
          phoneNumber: "Phone number already exists",
        }));
        return false;
      }
      if (checkRes?.error === true) {
        alert(
          checkRes?.message ||
            "Unable to verify phone number now. Please try again.",
        );
        return false;
      }
      return true;
    } catch (error) {
      console.error("Phone check failed:", error);
      alert("Unable to verify phone number now. Please try again.");
      return false;
    } finally {
      setIsCheckingPhone(false);
    }
  };

  const getDischargeUploadState = () => {
    const trimmedPdfTitle = pdfTitle.trim();
    const hasAnyUploadInput = Boolean(trimmedPdfTitle || pdfFile);
    const hasCompleteNewUpload = Boolean(trimmedPdfTitle && pdfFile);
    return { trimmedPdfTitle, hasAnyUploadInput, hasCompleteNewUpload };
  };

  const persistAddFlowProfileAndDischarge = async ({
    finalizeAddFlow = false,
    includeDischargeSummary = true,
  } = {}) => {
    if (!onSave || (includeDischargeSummary && !onUploadDischargeSummary)) {
      return { ok: false, patientId: "", dischargeSummaryId: "" };
    }

    const existingPatientId = String(createdPatientId || "");
    const savePayload = existingPatientId
      ? buildUpdatePayload(existingPatientId)
      : buildPayload();
    const hasSelectedExistingSummary = Boolean(
      selectedDischargeSummaryId ||
      resolveDischargeSummaryId(selectedExistingSummary),
    );
    savePayload.hasDischargeSummary = Boolean(
      includeDischargeSummary || hasSelectedExistingSummary,
    );
    if (existingPatientId) {
      savePayload._id = existingPatientId;
      savePayload.id = existingPatientId;
      savePayload.userID = existingPatientId;
    }

    const saveRes = await onSave(savePayload, { suppressSuccessToast: true });
    if (!saveRes || saveRes.error !== false) {
      return { ok: false, patientId: "", dischargeSummaryId: "" };
    }

    const resolvedPatientId = String(
      resolvePatientRecordId(saveRes?.data) || existingPatientId || "",
    );
    if (!resolvedPatientId) {
      alert("Unable to resolve patient ID. Please try again.");
      return { ok: false, patientId: "", dischargeSummaryId: "" };
    }
    setCreatedPatientId(resolvedPatientId);

    if (!includeDischargeSummary) {
      setSelectedExistingSummary(null);
      setSelectedDischargeSummaryId("");
      return {
        ok: true,
        patientId: resolvedPatientId,
        dischargeSummaryId: "",
      };
    }

    const uploadRes = await onUploadDischargeSummary(
      {
        patientID: resolvedPatientId,
        title: pdfTitle.trim(),
        file: pdfFile,
      },
      { finalizeAddFlow },
    );
    if (!uploadRes || uploadRes.error !== false) {
      return {
        ok: false,
        patientId: resolvedPatientId,
        dischargeSummaryId: "",
      };
    }

    const uploadedSummaryId = extractUploadedDischargeSummaryId(uploadRes);
    setSelectedExistingSummary(null);
    setSelectedDischargeSummaryId(uploadedSummaryId || "");

    return {
      ok: true,
      patientId: resolvedPatientId,
      dischargeSummaryId: String(uploadedSummaryId || ""),
    };
  };

  const resetAddDraftState = () => {
    setCurrentStep(1);
    setEntryMode("single");
    setCreatedPatientId("");
    setProfile(getDefaultProfile());
    setPdfTitle("");
    setPdfFile(null);
    setPdfPreviewUrl("");
    setPdfError("");
    setSelectedExistingSummary(null);
    setSelectedDischargeSummaryId("");
    setCarePlan(getDefaultCarePlan());
    resetTransientUiState();
    resetBulkState();
  };

  const handleRequestClose = () => {
    resetTransientUiState();
    if (!isAddMode) {
      onClose();
      return;
    }

    const shouldDiscard = window.confirm(
      "Data will not be saved. Do you want to discard?",
    );
    if (!shouldDiscard) return;

    resetAddDraftState();
    onClose();
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      const step1Errors = validateStep1();
      if (Object.keys(step1Errors).length > 0) {
        setErrors(step1Errors);
        return;
      }
      setErrors({});

      if (isAddMode) {
        const phoneAllowed = await validatePhoneUniqueness();
        if (!phoneAllowed) return;
      }

      if (!isAddMode && onSave) {
        const payload = createdPatientId
          ? buildUpdatePayload(createdPatientId)
          : buildPayload();
        if (createdPatientId) {
          payload._id = createdPatientId;
          payload.id = createdPatientId;
          payload.userID = createdPatientId;
        }
        const res = await onSave(payload);
        if (!res || res.error !== false) return;

        const newId = resolvePatientRecordId(res?.data);
        const effectivePatientId = String(
          newId || createdPatientId || payload?._id || "",
        );
        if (effectivePatientId) setCreatedPatientId(effectivePatientId);

        if (effectivePatientId) {
          try {
            const freshRes = await getPatientById(effectivePatientId);
            if (freshRes && freshRes.error === false) {
              const b = freshRes.data;
              const pInfo = b.personalInfo || {};
              const basicInfo = pInfo.basicInfo || {};
              const addrInfo = pInfo.addressInfo || {};
              const nameInfo = basicInfo.nameInfo || {};
              const otherInfo = basicInfo.otherInfo || {};

              let dobStr = "";
              if (otherInfo.dob)
                dobStr = new Date(otherInfo.dob).toISOString().split("T")[0];

              const fullPhone = String(otherInfo.phone || "");
              let phoneCode = "91";
              let phoneNumber = "";
              if (fullPhone.length > 10) {
                phoneNumber = fullPhone.slice(-10);
                phoneCode = fullPhone.slice(0, fullPhone.length - 10);
              } else {
                phoneNumber = fullPhone;
              }

              setProfile((prev) => ({
                ...prev,
                abhaNumber: b.ABHA?.number || prev.abhaNumber,
                mrn: b.mrn || b.patientID || prev.mrn,
                title: nameInfo.title || prev.title,
                name: nameInfo.fullName || prev.name,
                dob: dobStr || prev.dob,
                gender: otherInfo.gender || prev.gender,
                phoneCode,
                phoneNumber,
                flatNo: addrInfo.unitNumber || prev.flatNo,
                address: addrInfo.address1 || prev.address,
                city: addrInfo.city || prev.city,
                district: addrInfo.district || prev.district,
                state: addrInfo.state || prev.state,
                pincode: String(addrInfo.pincode || "") || prev.pincode,
                country: addrInfo.country || prev.country,
                coordinates: addrInfo.coordinates || prev.coordinates,
              }));
            }
          } catch (err) {
            console.error("Error syncing with getPatientById:", err);
          }
        }
      }
    }

    if (currentStep === 2) {
      const { trimmedPdfTitle, hasAnyUploadInput, hasCompleteNewUpload } =
        getDischargeUploadState();
      if (hasCompleteNewUpload && pdfFile?.size > MAX_PDF_SIZE_BYTES) {
        setPdfError("PDF size must be 10MB or less.");
        alert("PDF size must be 10MB or less.");
        return;
      }

      if (isPrescribeMode) {
        if (!createdPatientId) {
          alert("Patient record is missing. Please reopen and try again.");
          return;
        }

        if (existingDischargeSummariesLoading) {
          alert("Please wait while discharge summaries are loading.");
          return;
        }

        let explicitSummaryId = String(
          selectedDischargeSummaryId ||
            resolveDischargeSummaryId(selectedExistingSummary) ||
            "",
        ).trim();
        let hasUploadedSummaryInThisStep = false;

        if (hasAnyUploadInput && !hasCompleteNewUpload) {
          alert("Please provide a Document Title and upload a PDF.");
          return;
        }

        if (hasCompleteNewUpload && onUploadDischargeSummary) {
          setIsUploadingDischargeSummary(true);
          try {
            const uploadRes = await onUploadDischargeSummary({
              patientID: createdPatientId,
              title: trimmedPdfTitle,
              file: pdfFile,
            });
            if (!uploadRes || uploadRes.error !== false) return;
            const uploadedSummaryId =
              extractUploadedDischargeSummaryId(uploadRes);
            setSelectedExistingSummary(null);
            setSelectedDischargeSummaryId(uploadedSummaryId);
            explicitSummaryId = String(uploadedSummaryId || "").trim();
            hasUploadedSummaryInThisStep = true;
          } finally {
            setIsUploadingDischargeSummary(false);
          }
        }

        const requiresDischargeSummaryToProceed =
          !isProfileCompleteForPrescription;
        if (
          requiresDischargeSummaryToProceed &&
          !explicitSummaryId &&
          !hasUploadedSummaryInThisStep
        ) {
          alert(
            "Discharge summary is required to continue because patient profile is incomplete.",
          );
          return;
        }
      } else if (isAddMode) {
        const isSkippingDischarge = !hasAnyUploadInput;
        if (!isSkippingDischarge && !hasCompleteNewUpload) {
          alert("Please provide a Document Title and upload a PDF.");
          return;
        }
        if (!onSave) return;

        setIsUploadingDischargeSummary(true);
        try {
          const persisted = await persistAddFlowProfileAndDischarge({
            finalizeAddFlow: false,
            includeDischargeSummary: hasCompleteNewUpload,
          });
          if (!persisted.ok) return;
          if (isSkippingDischarge) {
            showToast("Patient profile saved successfully!", "Success");
            onClose();
            return;
          }
        } finally {
          setIsUploadingDischargeSummary(false);
        }
      }
    }

    setCurrentStep((prev) => prev + 1);
  };

  const getRoleAndSubRoleForCarePlan = () => {
    const category = String(carePlan.serviceCategory || "").toLowerCase();
    if (category === "physio" || category === "physiotherapy") {
      return { role: "physiotherapist", subRole: "physiotherapist" };
    }
    if (
      String(carePlan.nurseSpecialist || "").toLowerCase() ===
      "nursing-assistant"
    ) {
      return { role: "nurse", subRole: "nursing-assistant" };
    }
    return { role: "nurse", subRole: "nurse" };
  };

  const buildAddressInfoForVisit = () => {
    const fromProfile = {
      address1: profile.address || "",
      address2: "",
      district: profile.district || "",
      city: profile.city || "",
      state: profile.state || "",
      pincode: profile.pincode || "",
      landmark: "",
      coordinates: profile.coordinates || {},
    };
    if (fromProfile.address1) return fromProfile;
    return initialPatient?.personalInfo?.addressInfo || {};
  };

  const submitCarePlanVisit = async (patientId, options = {}) => {
    const category = String(carePlan.serviceCategory || "").trim();
    if (!category) return true; // No care-plan selection yet; keep existing behavior.
    if (!["ready", "not_required"].includes(cancellationPolicyStatus)) {
      alert(
        "The patient cancellation policy could not be confirmed. Please retry before submitting the care plan.",
      );
      return false;
    }

    const isNurseCategory = category === "Nursing Care";
    const isPhysioCategory =
      category === "Physio" || category === "Physiotherapy";
    if (!isNurseCategory && !isPhysioCategory) {
      return true;
    }

    let totalDays = 0;
    let dutyType = "";
    let serviceFees = [];
    let feesPerDay = 0;
    let shiftType = "";
    let visitServices = [];
    let foodAccommodation = [];
    let travelAllowance = [];
    let sessionFrequency = "";
    let sessionDuration = 0;
    let sessionDurationType = "";
    let requestedDate = "";
    let assessmentServices = [];
    let sessionFee = 0;

    if (isPhysioCategory) {
      const selectedModules = Array.isArray(carePlan.physioModule)
        ? carePlan.physioModule
        : [];
      if (!selectedModules.length) {
        alert("Please select at least one service.");
        return false;
      }
      const selectedModuleSet = new Set(
        selectedModules
          .map((item) => String(item || "").trim())
          .filter(Boolean),
      );
      const selectedPhysioServices = (physioModuleOptions || [])
        .filter((item) => selectedModuleSet.has(String(item.value)))
        .map((item) => ({
          name: String(item.label || item.value || "").trim(),
          fee: Number(item.fee || 0),
        }))
        .filter((item) => item.name);
      if (!selectedPhysioServices.length) {
        alert("Please select valid service options.");
        return false;
      }

      const durationValue = Number(carePlan.physioDurationValue || 0);
      if (!Number.isFinite(durationValue) || durationValue <= 0) {
        alert("Please enter valid duration.");
        return false;
      }

      const frequencyRaw = String(carePlan.physioFrequency || "").trim();
      if (!frequencyRaw) {
        alert("Please select frequency.");
        return false;
      }

      const frequencyMap = {
        "Every day": "Daily",
        "Alternative days": "Alternative Days",
        "Twice in week": "Twice in a Week",
        "Thrice in week": "Thrice in a Week",
      };
      const durationTypeRaw = String(
        carePlan.physioDurationType || "Days",
      ).trim();
      const durationTypeLower = durationTypeRaw.toLowerCase();
      let durationInDays = durationValue;
      if (durationTypeLower === "weeks") durationInDays = durationValue * 7;
      if (durationTypeLower === "months") durationInDays = durationValue * 30;

      dutyType = "regular";
      totalDays = Math.max(1, Math.round(durationInDays));
      serviceFees = selectedPhysioServices.map((item) => ({
        service: item.name,
        fee: Number(item?.fee || 0),
      }));
      visitServices = selectedPhysioServices.map((item) => item.name);
      assessmentServices = selectedPhysioServices;
      sessionFee = selectedPhysioServices.reduce(
        (sum, item) => sum + Number(item?.fee || 0),
        0,
      );
      feesPerDay = sessionFee;
      foodAccommodation = [];
      travelAllowance = [];
      sessionFrequency = frequencyMap[frequencyRaw] || frequencyRaw;
      sessionDuration = durationValue;
      sessionDurationType = durationTypeLower;
      requestedDate = moment().format("YYYY-MM-DD");
    }

    if (isNurseCategory) {
      const nurseSpecialist = String(carePlan.nurseSpecialist || "").trim();
      if (!nurseSpecialist) {
        alert("Please select a specialist.");
        return false;
      }

      const nurseType = String(carePlan.nurseType || "").trim();
      if (!nurseType) {
        alert("Please select a nurse service type.");
        return false;
      }

      totalDays = Number(carePlan.nurseDays || 0);
      if (totalDays <= 0) {
        alert("Please enter valid days.");
        return false;
      }

      if (nurseType === "One Time") {
        dutyType = "oneTime";
        const selectedServices = Array.isArray(carePlan.nurseOneTimeServices)
          ? carePlan.nurseOneTimeServices
          : [];
        if (!selectedServices.length) {
          alert("For Nurse One-Time, please select one-time services.");
          return false;
        }

        const feeByService = new Map(
          (nurseOneTimeServiceOptions || []).map((opt) => [
            String(opt.value),
            Number(opt.fee || 0),
          ]),
        );

        serviceFees = selectedServices.map((service) => ({
          service,
          fee: feeByService.get(String(service)) || 0,
        }));
        visitServices = serviceFees.map((item) => item.service);
        feesPerDay = serviceFees.reduce(
          (sum, item) => sum + Number(item.fee || 0),
          0,
        );
      } else if (nurseType === "Regular") {
        dutyType = "regular";
        if (!selectedRegularDuty) {
          alert("Please select a regular duty type.");
          return false;
        }
        if (!selectedRegularSlot) {
          alert("Please select a regular service.");
          return false;
        }
        const selectedRegularServices = safeArr(selectedRegularSlot?.services)
          .map((item) => String(item || "").trim())
          .filter(Boolean);
        if (!selectedRegularServices.length) {
          alert("Selected service does not have configured services.");
          return false;
        }
        feesPerDay = Number(selectedRegularSlot?.fee || 0);
        const shift = String(selectedRegularSlot?.shift || "").trim();
        const type = String(selectedRegularSlot?.type || "").trim();
        shiftType = [shift, type].filter(Boolean).join(",");
        visitServices = selectedRegularServices;
        serviceFees = [];
        foodAccommodation = safeArr(selectedRegularSlot?.foodAccommodation);
        travelAllowance = safeArr(selectedRegularSlot?.travelAllowance);
      } else {
        alert("Unsupported nurse service type.");
        return false;
      }
    }

    const { role, subRole } = getRoleAndSubRoleForCarePlan();
    const chosenDischargeSummaryId = String(
      options?.dischargeSummaryId ||
        selectedDischargeSummaryId ||
        resolveDischargeSummaryId(selectedExistingSummary) ||
        "",
    ).trim();

    const visitPayload = {
      role,
      subRole,
      dutyType,
      patientID: patientId,
      days: totalDays,
      feesPerDay,
      services: serviceFees,
      addressInfo: buildAddressInfoForVisit(),
      ...(shiftType ? { shiftType } : {}),
      ...(visitServices.length ? { visitServices } : {}),
      ...(foodAccommodation.length ? { foodAccommodation } : {}),
      ...(travelAllowance.length ? { travelAllowance } : {}),
      ...(String(carePlan.patientInstructions || "").trim()
        ? {
            instructionsForPatient: String(carePlan.patientInstructions).trim(),
          }
        : {}),
      ...(isPhysioCategory
        ? {
            sessionFrequency,
            sessionDuration,
            sessionDurationType,
            requestedDate,
            assessmentServices,
            sessionFee,
          }
        : {}),
    };
    if (chosenDischargeSummaryId) {
      visitPayload.dischargeSummary = chosenDischargeSummaryId;
    }

    setIsSubmittingCarePlan(true);
    try {
      const visitRes = await createVisitForPatient(visitPayload);
      if (!visitRes || visitRes.error) {
        alert(visitRes?.message || "Failed to save care plan prescription.");
        return false;
      }
      return true;
    } catch (error) {
      console.error("createVisitForPatient error:", error);
      alert("Failed to save care plan prescription.");
      return false;
    } finally {
      setIsSubmittingCarePlan(false);
    }
  };

  const handleSkipCarePlan = () => {
    if (!isAddMode || currentStep !== 3) return;
    const shouldSkip = window.confirm(
      "Care plan will be skipped for now. Do you want to continue?",
    );
    if (!shouldSkip) return;

    showToast("Patient profile saved. Care plan skipped.", "Info");
    onClose();
  };

  const handleSubmit = async () => {
    if (isPrescribeMode) {
      const patientId = String(
        createdPatientId || resolvePatientRecordId(initialPatient) || "",
      );
      if (!patientId) {
        alert(
          "Patient record is missing. Please close and reopen prescription.",
        );
        return;
      }
      const ok = await submitCarePlanVisit(patientId);
      if (!ok) return;
      showToast("Care plan prescription saved successfully!", "Success");
      onClose();
      return;
    }

    if (!isAddMode) {
      const structuredPayload = buildPayload();
      // console.log("Submitting Final Step Data:", structuredPayload);
      onClose();
      return;
    }

    let effectivePatientId = String(createdPatientId || "").trim();
    let effectiveSummaryId = String(
      selectedDischargeSummaryId ||
        resolveDischargeSummaryId(selectedExistingSummary) ||
        "",
    ).trim();

    if (!effectivePatientId) {
      const step1Errors = validateStep1();
      if (Object.keys(step1Errors).length > 0) {
        setErrors(step1Errors);
        setCurrentStep(1);
        return;
      }

      const phoneAllowed = await validatePhoneUniqueness();
      if (!phoneAllowed) {
        setCurrentStep(1);
        return;
      }

      const { hasAnyUploadInput, hasCompleteNewUpload } =
        getDischargeUploadState();
      if (hasAnyUploadInput && !hasCompleteNewUpload) {
        setCurrentStep(2);
        alert("Please provide a Document Title and upload a PDF.");
        return;
      }
      if (hasCompleteNewUpload && pdfFile.size > MAX_PDF_SIZE_BYTES) {
        setCurrentStep(2);
        setPdfError("PDF size must be 10MB or less.");
        alert("PDF size must be 10MB or less.");
        return;
      }

      setIsUploadingDischargeSummary(true);
      try {
        const persisted = await persistAddFlowProfileAndDischarge({
          finalizeAddFlow: true,
          includeDischargeSummary: hasCompleteNewUpload,
        });
        if (!persisted.ok) {
          setCurrentStep(2);
          return;
        }
        effectivePatientId = String(persisted.patientId || "").trim();
        effectiveSummaryId = String(persisted.dischargeSummaryId || "").trim();
      } finally {
        setIsUploadingDischargeSummary(false);
      }
    }

    if (!effectivePatientId) {
      alert("Patient record is missing. Please try again.");
      return;
    }

    const selectedCategory = String(carePlan.serviceCategory || "").trim();
    if (!selectedCategory) {
      showToast(
        "Please complete Care Plan Prescription or use Skip Care Plan.",
        "Error",
      );
      return;
    }

    const carePlanOk = await submitCarePlanVisit(effectivePatientId, {
      dischargeSummaryId: effectiveSummaryId,
    });
    if (!carePlanOk) return;

    showToast("Care plan prescription saved successfully!", "Success");
    onClose();
  };

  const handleEditUpdate = async () => {
    const patientId = String(
      createdPatientId || resolvePatientRecordId(initialPatient) || "",
    );
    if (!patientId) {
      alert("Patient record is missing. Please close and reopen edit.");
      return;
    }

    const step1Errors = validateStep1();
    if (Object.keys(step1Errors).length > 0) {
      setErrors(step1Errors);
      return;
    }
    setErrors({});

    const phoneAllowed = await validatePhoneUniqueness();
    if (!phoneAllowed) return;

    if (!onSave) return;
    const payload = buildUpdatePayload(patientId);
    payload._id = patientId;
    payload.id = patientId;
    payload.userID = patientId;

    const res = await onSave(payload);
    if (res && res.error === false) {
      onClose();
    }
  };

  const { minDobMoment, maxDobMoment } = getDobBounds();
  const minDob = formatDobBound(minDobMoment) || "1900-01-01";
  const today = formatDobBound(maxDobMoment) || moment().format("YYYY-MM-DD");
  const isBulkMode = isAddMode && entryMode === "bulk";

  const bulkDraggerProps = {
    name: "file",
    multiple: false,
    accept: ".csv,.xlsx,.xls",
    fileList: bulkFileList,
    beforeUpload: (file) => handleBulkFile(file),
    onRemove: () => {
      resetBulkState();
    },
  };

  const renderStepIndicator = () => {
    const steps = isPrescribeMode
      ? [
          { stepNum: 2, label: "Discharge Summary" },
          { stepNum: 3, label: "Care Plan Prescription" },
        ]
      : [
          { stepNum: 1, label: "Profile" },
          { stepNum: 2, label: "Discharge Summary" },
          { stepNum: 3, label: "Care Plan Prescription" },
        ];
    return (
      <div className="flex items-center justify-between mb-8 relative">
        <div className="absolute left-0 right-0 top-1/2 h-[2px] bg-gray-100 -z-10 transform -translate-y-1/2"></div>
        {steps.map(({ stepNum, label }) => {
          const isActive = currentStep === stepNum;
          const isCompleted = currentStep > stepNum;

          return (
            <div
              key={`${stepNum}-${label}`}
              className="flex flex-col items-center bg-white px-4"
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-[13px] font-semibold transition-colors duration-300
                  ${
                    isActive
                      ? "bg-[#3E57E8] text-white shadow-md"
                      : isCompleted
                        ? "bg-green-500 text-white"
                        : "bg-gray-100 text-gray-400"
                  }`}
              >
                {isCompleted ? (
                  <MdCheckCircle size={18} />
                ) : isPrescribeMode ? (
                  stepNum - 1
                ) : (
                  stepNum
                )}
              </div>
              <span
                className={`text-[11px] font-poppins mt-2 ${
                  isActive ? "text-[#3E57E8] font-medium" : "text-gray-400"
                }`}
              >
                {label}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderEntryModeToggle = () => (
    <div className="mb-5 flex justify-center">
      <div className="inline-flex bg-gray-100 rounded-xl p-1">
        <button
          type="button"
          onClick={() => {
            setEntryMode("single");
            resetBulkState();
          }}
          className={`px-4 py-1.5 text-[12px] font-poppins rounded-lg transition-colors ${
            entryMode === "single"
              ? "bg-white text-[#3E57E8] shadow-sm"
              : "text-gray-600 hover:text-gray-800"
          }`}
        >
          Single
        </button>
        <button
          type="button"
          onClick={() => setEntryMode("bulk")}
          className={`px-4 py-1.5 text-[12px] font-poppins rounded-lg transition-colors ${
            entryMode === "bulk"
              ? "bg-white text-[#3E57E8] shadow-sm"
              : "text-gray-600 hover:text-gray-800"
          }`}
        >
          Bulk Upload
        </button>
      </div>
    </div>
  );

  // ---------------- STEP 1 UI (UNCHANGED) ----------------
  const renderStep1 = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 animate-fadeIn">
      <div>
        <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">
          ABHA Number
        </label>
        <input
          type="text"
          name="abhaNumber"
          value={profile.abhaNumber}
          onChange={handleProfileChange}
          className={inputClass}
          placeholder="Enter ABHA number"
        />
      </div>
      <div>
        <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">
          MRN (Medical Record Number)
        </label>
        <input
          type="text"
          name="mrn"
          value={profile.mrn}
          onChange={handleProfileChange}
          className={inputClass}
          placeholder="Enter MRN"
        />
      </div>

      <div>
        <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">
          Title & Patient Name (As in Aadhar Card){" "}
          <span className="text-red-500">*</span>
        </label>
        <div
          className={`flex border rounded-xl bg-[#F6F8FC] overflow-hidden focus-within:border-[#3E57E8] focus-within:ring-2 focus-within:ring-[#3E57E8]/15 transition-all ${
            errors.name ? "border-red-500" : "border-gray-200"
          }`}
        >
          <select
            name="title"
            value={profile.title}
            onChange={handleProfileChange}
            className="bg-transparent text-[13px] font-poppins px-3 outline-none border-r border-gray-200 min-w-[70px]"
          >
            {titleOptions.length > 0 ? (
              titleOptions.map((item, index) => (
                <option key={`${item.value}-${index}`} value={item.value}>
                  {item.label}
                </option>
              ))
            ) : (
              <>
                <option value="Mr">Mr</option>
                <option value="Mrs">Mrs</option>
                <option value="Ms">Ms</option>
              </>
            )}
          </select>
          <input
            type="text"
            name="name"
            value={profile.name}
            onChange={handleProfileChange}
            placeholder="Full Name"
            className="bg-transparent text-[13px] font-poppins px-3 py-2.5 outline-none flex-1 w-full"
          />
        </div>
        {errors.name && (
          <p className="text-red-500 text-[10px] mt-1 font-poppins">
            {errors.name}
          </p>
        )}
      </div>

      <div>
        <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">
          Date of Birth <span className="text-red-500">*</span>
        </label>
        <input
          type="date"
          name="dob"
          min={minDob}
          max={today}
          value={profile.dob}
          onChange={handleProfileChange}
          className={`${inputClass} ${errors.dob ? "border-red-500" : ""}`}
        />
        {errors.dob && (
          <p className="text-red-500 text-[10px] mt-1 font-poppins">
            {errors.dob}
          </p>
        )}
      </div>

      <div>
        <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">
          Gender <span className="text-red-500">*</span>
        </label>
        <select
          name="gender"
          value={profile.gender}
          onChange={handleProfileChange}
          className={inputClass}
        >
          <option value="male">Male</option>
          <option value="female">Female</option>
        </select>
      </div>

      <div>
        <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">
          Phone Number <span className="text-red-500">*</span>
        </label>
        <div
          className={`flex border rounded-xl bg-[#F6F8FC] overflow-hidden focus-within:border-[#3E57E8] focus-within:ring-2 focus-within:ring-[#3E57E8]/15 transition-all ${
            errors.phoneNumber ? "border-red-500" : "border-gray-200"
          }`}
        >
          <select
            name="phoneCode"
            value={profile.phoneCode}
            onChange={handleProfileChange}
            className="bg-transparent text-[13px] font-poppins px-2 outline-none border-r border-gray-200"
          >
            {phoneCodeOptions.length > 0 ? (
              phoneCodeOptions.map((item, index) => {
                const codeValue = normalizePhoneCodeValue(item?.value);
                return (
                  <option key={`${item.value}-${index}`} value={codeValue}>
                    {getPhoneCodeLabel(item)}
                  </option>
                );
              })
            ) : (
              <>
                <option value="91">+91 (IN)</option>
                <option value="1">+1 (US)</option>
                <option value="44">+44 (UK)</option>
              </>
            )}
          </select>
          <input
            type="tel"
            name="phoneNumber"
            value={profile.phoneNumber}
            onChange={handleProfileChange}
            maxLength={10}
            placeholder="10-digit number"
            className="bg-transparent text-[13px] font-poppins px-3 py-2.5 outline-none flex-1"
          />
        </div>
        {errors.phoneNumber && (
          <p className="text-red-500 text-[10px] mt-1 font-poppins">
            {errors.phoneNumber}
          </p>
        )}
        {isCheckingPhone ? (
          <p className="text-[#3E57E8] text-[10px] mt-1 font-poppins">
            Checking phone number...
          </p>
        ) : null}
      </div>

      <div>
        <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">
          Emergency Contact
        </label>
        <div className="flex border border-gray-200 rounded-xl bg-[#F6F8FC] overflow-hidden focus-within:border-[#3E57E8] focus-within:ring-2 focus-within:ring-[#3E57E8]/15 transition-all">
          <select
            name="emergencyCode"
            value={profile.emergencyCode}
            onChange={handleProfileChange}
            className="bg-transparent text-[13px] font-poppins px-2 outline-none border-r border-gray-200"
          >
            {phoneCodeOptions.length > 0 ? (
              phoneCodeOptions.map((item, index) => {
                const codeValue = normalizePhoneCodeValue(item?.value);
                return (
                  <option key={`em-${item.value}-${index}`} value={codeValue}>
                    {getPhoneCodeLabel(item)}
                  </option>
                );
              })
            ) : (
              <>
                <option value="91">+91 (IN)</option>
                <option value="1">+1 (US)</option>
                <option value="44">+44 (UK)</option>
              </>
            )}
          </select>
          <input
            type="tel"
            name="emergencyNumber"
            value={profile.emergencyNumber}
            onChange={handleProfileChange}
            placeholder="10-digit number"
            maxLength={10}
            className="bg-transparent text-[13px] font-poppins px-3 py-2.5 outline-none flex-1"
          />
        </div>
      </div>

      <div>
        <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">
          Flat No / Door No
        </label>
        <input
          type="text"
          name="flatNo"
          value={profile.flatNo}
          onChange={handleProfileChange}
          className={inputClass}
          placeholder="e.g. Flat 4B, 2nd Floor"
        />
      </div>

      <div className="md:col-span-2 relative" ref={addressRef}>
        <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">
          Address <span className="text-red-500">*</span>
        </label>

        <div className="mb-3 flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="inline-flex w-full rounded-xl bg-gray-100 p-1 sm:w-auto">
            {[
              { value: "manual", label: "Manual" },
              // Temporarily hide the map picker until its browser key is configured.
              // { value: "map", label: "Map" },
              { value: "google", label: "Google Map Link" },
            ].map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  setAddressMode(option.value);
                  setGoogleMapsLinkError("");
                  setAddressSuggestions([]);
                  if (option.value === "map") setIsLocationPickerOpen(true);
                  if (option.value === "google")
                    setIsGoogleMapsLinkModalOpen(true);
                }}
                className={`flex-1 rounded-lg px-3 py-2 font-poppins text-[11px] font-medium transition-colors sm:flex-none ${
                  addressMode === option.value
                    ? "bg-white text-[#3E57E8] shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          {addressMode === "map" && String(profile.address || "").trim() ? (
            <button
              type="button"
              onClick={() => setIsLocationPickerOpen(true)}
              className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg border border-[#3E57E8]/20 bg-blue-50 px-3 py-2 font-poppins text-[11px] font-medium text-[#3E57E8] hover:bg-blue-100"
            >
              <MdLocationOn size={15} />
              Open Map Picker
            </button>
          ) : addressMode === "google" &&
            String(profile.address || "").trim() ? (
            <button
              type="button"
              onClick={() => setIsGoogleMapsLinkModalOpen(true)}
              className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg border border-[#3E57E8]/20 bg-blue-50 px-3 py-2 font-poppins text-[11px] font-medium text-[#3E57E8] hover:bg-blue-100"
            >
              <MdLink size={15} />
              Open Google Map Link
            </button>
          ) : null}
        </div>

        {addressMode === "map" && !String(profile.address || "").trim() ? (
          <button
            type="button"
            onClick={() => setIsLocationPickerOpen(true)}
            className="mb-3 inline-flex items-center gap-1.5 rounded-lg border border-[#3E57E8]/20 bg-blue-50 px-3 py-2 font-poppins text-[11px] font-medium text-[#3E57E8] hover:bg-blue-100"
          >
            <MdLocationOn size={15} />
            Open Map Picker
          </button>
        ) : addressMode === "google" &&
          !String(profile.address || "").trim() ? (
          <button
            type="button"
            onClick={() => setIsGoogleMapsLinkModalOpen(true)}
            className="mb-3 inline-flex items-center gap-1.5 rounded-lg border border-[#3E57E8]/20 bg-blue-50 px-3 py-2 font-poppins text-[11px] font-medium text-[#3E57E8] hover:bg-blue-100"
          >
            <MdLink size={15} />
            Open Google Map Link
          </button>
        ) : null}

        <div className="relative">
          <input
            type="text"
            name="address"
            value={profile.address}
            onChange={
              addressMode === "manual"
                ? onAddressChange
                : (event) => {
                    const address = event.target.value;
                    setProfile((prev) => ({ ...prev, address }));
                    setErrors((prev) => {
                      if (!prev.address) return prev;
                      const nextErrors = { ...prev };
                      delete nextErrors.address;
                      return nextErrors;
                    });
                  }
            }
            className={`${inputClass} ${errors.address ? "border-red-500" : ""}`}
            placeholder={
              addressMode === "manual"
                ? "Enter the patient's address or search for a location..."
                : "Address from the selected coordinates..."
            }
            autoComplete="off"
          />
          {addressMode === "manual" && isSearchingAddress ? (
            <div className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin rounded-full border-2 border-[#3E57E8] border-t-transparent" />
          ) : null}
        </div>
        {addressMode === "manual" && addressSuggestions.length > 0 && (
          <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
            {addressSuggestions.map((item, idx) => (
              <div
                key={idx}
                className="px-4 py-2 text-[12px] font-poppins hover:bg-gray-50 cursor-pointer border-b last:border-0"
                onClick={() => handleSelectAddressSuggestion(item)}
              >
                {item.description}
              </div>
            ))}
          </div>
        )}
        {hasValidCoordinates(profile.coordinates) ? (
          <span className="mt-2 inline-flex items-center gap-1 font-poppins text-[11px] font-medium text-green-600">
            <MdCheckCircle size={15} />
            Location coordinates saved
          </span>
        ) : null}
        {errors.address && (
          <p className="text-red-500 text-[10px] mt-1 font-poppins">
            {errors.address}
          </p>
        )}
      </div>
    </div>
  );

  const renderBulkUpload = () => (
    <div className="animate-fadeIn">
      <div className="text-center mb-5">
        <h3 className="text-[15px] font-semibold text-gray-800 font-poppins">
          Import Bulk Patients
        </h3>
        <p className="font-poppins text-[#8177A8] text-[12px] mt-2">
          Download the sample template, fill it, then upload CSV/XLSX.
        </p>
        <button
          type="button"
          onClick={downloadBulkSampleExcel}
          className="mt-3 inline-flex items-center gap-2 bg-[#3E57E8] text-white text-[12px] font-poppins px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <FiDownload size={14} />
          Download Sample Template
        </button>
      </div>

      <Dragger
        {...bulkDraggerProps}
        disabled={bulkSubmitting || bulkParsing}
        style={{ width: "100%", padding: "15px 0" }}
      >
        <div className="w-14 h-14 bg-blue-100 text-[#3E57E8] rounded-full flex items-center justify-center mb-3 mx-auto">
          <MdFileUpload size={26} />
        </div>
        <p className="ant-upload-text font-poppins text-[12px]">
          Choose a file or drag it here (.csv / .xlsx)
        </p>
      </Dragger>

      {bulkFileList.length > 0 && (
        <div className="mt-4">
          {bulkErrors.length > 0 ? (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-[12px] font-poppins text-red-700">
                Found {bulkErrors.length} error(s). Fix and re-upload the file.
              </p>
              <ul className="list-disc pl-5 mt-2">
                {bulkErrors.slice(0, 10).map((errorText, idx) => (
                  <li
                    key={idx}
                    className="text-[12px] font-poppins text-red-700"
                  >
                    {errorText}
                  </li>
                ))}
              </ul>
              {bulkErrors.length > 10 ? (
                <p className="text-[11px] font-poppins text-red-700 mt-2">
                  ...and {bulkErrors.length - 10} more
                </p>
              ) : null}
            </div>
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-md p-3">
              <p className="text-[12px] font-poppins text-green-700">
                File validated successfully. {bulkValidatedRows.length} row(s)
                ready to upload.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const formatSummaryDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const renderStep2 = () => (
    <div className="flex flex-col animate-fadeIn h-full min-h-[400px]">
      <h3 className="text-[15px] font-semibold text-gray-800 font-poppins mb-4">
        Discharge Summary
      </h3>

      {isPrescribeMode &&
        (existingDischargeSummariesLoading ||
          existingDischargeSummariesError ||
          (Array.isArray(existingDischargeSummaries) &&
            existingDischargeSummaries.length > 0)) && (
          <div className="mb-5 rounded-xl border border-gray-200 bg-[#FCFDFF] ">
            <div className="px-3 py-2.5 border-b border-gray-100 flex items-center justify-between">
              <p className="text-[12px] font-semibold text-gray-800 font-poppins">
                Previous Discharge Summaries
              </p>
              {selectedExistingSummary ? (
                <button
                  type="button"
                  onClick={clearExistingSummarySelection}
                  className="text-[11px] text-[#3E57E8] hover:text-[#2E45C7] font-poppins"
                >
                  Clear selection
                </button>
              ) : (
                <span className="text-[11px] text-gray-400 font-poppins">
                  No selection
                </span>
              )}
            </div>

            <div className="p-3">
              {existingDischargeSummariesLoading ? (
                <div className="text-[12px] text-[#6b6b6b] italic">
                  Loading discharge summaries...
                </div>
              ) : existingDischargeSummariesError ? (
                <div className="text-[12px] text-red-500">
                  {existingDischargeSummariesError}
                </div>
              ) : Array.isArray(existingDischargeSummaries) &&
                existingDischargeSummaries.length > 0 ? (
                <div className="max-h-[175px] overflow-y-auto rounded-lg border border-gray-100 divide-y divide-gray-100 bg-white">
                  {existingDischargeSummaries.map((item, idx) => {
                    const selected =
                      getDischargeSummarySelectionKey(
                        selectedExistingSummary,
                      ) === getDischargeSummarySelectionKey(item);
                    return (
                      <button
                        key={item?._id || `${item?.title || "summary"}-${idx}`}
                        type="button"
                        onClick={() => handleExistingSummarySelection(item)}
                        className={`w-full text-left px-3 py-2.5 flex items-center gap-3 transition-colors ${selected ? "bg-[#EEF3FF]" : "hover:bg-gray-50"}`}
                      >
                        <span
                          className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${selected ? "border-[#3E57E8]" : "border-gray-300"}`}
                        >
                          {selected ? (
                            <span className="w-2 h-2 rounded-full bg-[#3E57E8]" />
                          ) : null}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-[12px] font-medium text-gray-800 font-poppins truncate">
                            {item?.title || "Untitled Summary"}
                          </span>
                          <span className="block text-[11px] text-gray-500 font-poppins">
                            {formatSummaryDate(item?.date || item?.createdAt)}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="text-[12px] text-[#6b6b6b] italic">
                  No discharge summaries uploaded.
                </div>
              )}
            </div>
          </div>
        )}

      <div className="mb-4">
        <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">
          Document Title
        </label>
        <input
          type="text"
          value={pdfTitle}
          onChange={(e) => setPdfTitle(e.target.value)}
          className={inputClass}
          placeholder="e.g., Post Surgery Report"
        />
        {isPrescribeMode ? (
          <p className="text-[11px] text-gray-400 font-poppins mt-1">
            {!isProfileCompleteForPrescription
              ? "Required: upload a discharge summary to continue to Care Plan Prescription."
              : "Optional: select a previous summary, upload a new one, or skip this step."}
          </p>
        ) : isAddMode ? (
          <p className="text-[11px] text-gray-400 font-poppins mt-1">
            Optional: upload now or skip this step.
          </p>
        ) : null}
      </div>

      {!pdfFile ? (
        <div className="flex-1 border-2 border-dashed border-gray-300 rounded-2xl flex flex-col items-center justify-center p-6 bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer relative">
          <input
            type="file"
            accept="application/pdf"
            onChange={handleFileUpload}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
          <div className="w-14 h-14 bg-blue-100 text-[#3E57E8] rounded-full flex items-center justify-center mb-4">
            <MdFileUpload size={28} />
          </div>
          <p className="text-[14px] font-medium text-gray-700 font-poppins">
            Click or drag PDF file to upload
          </p>
          <p className="text-[12px] text-gray-400 font-poppins mt-2">
            Maximum file size: 10MB
          </p>
        </div>
      ) : (
        <div className="flex flex-col flex-1 border border-gray-200 rounded-2xl overflow-hidden relative group min-h-[300px]">
          <div className="bg-gray-800 text-white px-4 py-3 flex justify-between items-center z-10 shrink-0">
            <span className="text-[13px] font-poppins truncate pr-4">
              {pdfFile.name} (Preview)
            </span>
            <button
              onClick={() => {
                setPdfFile(null);
                setPdfPreviewUrl("");
                setPdfTitle("");
                setPdfError("");
              }}
              className="text-[#ff5a5a] hover:text-[#ff3b3b] text-[12px] font-poppins bg-white/10 px-3 py-1 rounded-lg transition-colors cursor-pointer shrink-0"
            >
              Remove
            </button>
          </div>
          <iframe
            src={`${pdfPreviewUrl}#toolbar=0&navpanes=0&scrollbar=0`}
            className="w-full flex-1 border-0 bg-gray-100"
            title="PDF Preview"
          />
        </div>
      )}
      {pdfError ? (
        <p className="text-red-500 text-[11px] mt-2 font-poppins">{pdfError}</p>
      ) : null}
    </div>
  );

  const renderStep3 = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 animate-fadeIn">
      <div className="md:col-span-2">
        <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">
          Service Category
        </label>
        <select
          name="serviceCategory"
          value={carePlan.serviceCategory}
          onChange={handleCarePlanChange}
          className={inputClass}
          disabled={pricingLoading || serviceCategoryOptions.length === 0}
        >
          <option value="" disabled hidden>
            {pricingLoading
              ? "Loading categories..."
              : serviceCategoryOptions.length
                ? "Select Category"
                : "No service categories configured"}
          </option>
          {serviceCategoryOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {pricingError ? (
        <div className="md:col-span-2 text-[12px] text-red-500 font-poppins">
          {pricingError}
        </div>
      ) : null}

      {carePlan.serviceCategory === "Physio" && (
        <>
          <div className="md:col-span-2">
            <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">
              Services
            </label>
            <Select
              isMulti
              styles={reactSelectStyles}
              options={physioModuleOptions}
              formatOptionLabel={formatServiceOptionWithNotes}
              value={physioModuleOptions.filter((opt) =>
                (carePlan.physioModule || []).includes(opt.value),
              )}
              onChange={handlePhysioServicesMulti}
              isDisabled={pricingLoading || physioModuleOptions.length === 0}
              placeholder={
                pricingLoading
                  ? "Loading..."
                  : physioModuleOptions.length
                    ? "Select services..."
                    : "No priced physio services"
              }
            />
          </div>

          <div>
            <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">
              Duration
            </label>
            <div className="flex border border-gray-200 rounded-xl bg-[#F6F8FC] overflow-hidden focus-within:border-[#3E57E8] focus-within:ring-2 focus-within:ring-[#3E57E8]/15 transition-all">
              <input
                type="number"
                name="physioDurationValue"
                value={carePlan.physioDurationValue}
                onChange={handleCarePlanChange}
                className="bg-transparent text-[13px] font-poppins px-3 py-2.5 outline-none flex-1 border-r border-gray-200"
                placeholder="Number"
                min={1}
                max={99}
              />
              <select
                name="physioDurationType"
                value={carePlan.physioDurationType}
                onChange={handleCarePlanChange}
                className="bg-transparent text-[13px] font-poppins px-3 outline-none min-w-[90px]"
              >
                <option value="Days">Days</option>
                <option value="Weeks">Weeks</option>
                <option value="Months">Months</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">
              Frequency
            </label>
            <select
              name="physioFrequency"
              value={carePlan.physioFrequency}
              onChange={handleCarePlanChange}
              className={inputClass}
            >
              <option value="" disabled hidden>
                Select Frequency
              </option>
              {physioFrequencyOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </>
      )}

      {carePlan.serviceCategory === "Nursing Care" && (
        <>
          <div className="md:col-span-2">
            <h4 className="text-[13px] font-medium text-gray-800 mb-3 font-poppins">
              Specialist
            </h4>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="nurseSpecialist"
                  value="nurse"
                  checked={carePlan.nurseSpecialist === "nurse"}
                  onChange={handleCarePlanChange}
                  className="w-4 h-4 text-[#3E57E8] focus:ring-[#3E57E8]"
                />
                <span className="text-[13px] font-poppins text-gray-700">
                  Nurse
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="nurseSpecialist"
                  value="nursing-assistant"
                  checked={carePlan.nurseSpecialist === "nursing-assistant"}
                  onChange={handleCarePlanChange}
                  className="w-4 h-4 text-[#3E57E8] focus:ring-[#3E57E8]"
                />
                <span className="text-[13px] font-poppins text-gray-700">
                  Nursing Assistant
                </span>
              </label>
            </div>
          </div>

          <div className="md:col-span-2">
            <h4 className="text-[13px] font-medium text-gray-800 mb-3 font-poppins">
              Service Type
            </h4>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="nurseType"
                  value="Regular"
                  checked={carePlan.nurseType === "Regular"}
                  onChange={handleCarePlanChange}
                  className="w-4 h-4 text-[#3E57E8] focus:ring-[#3E57E8]"
                />
                <span className="text-[13px] font-poppins text-gray-700">
                  Regular Duty
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="nurseType"
                  value="One Time"
                  checked={carePlan.nurseType === "One Time"}
                  onChange={handleCarePlanChange}
                  className="w-4 h-4 text-[#3E57E8] focus:ring-[#3E57E8]"
                />
                <span className="text-[13px] font-poppins text-gray-700">
                  One Time Service
                </span>
              </label>
            </div>
          </div>

          {carePlan.nurseType === "Regular" && (
            <>
              <div className="md:col-span-2">
                <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">
                  Duty Shifts
                </label>
                <select
                  name="nurseRegularDutyKey"
                  value={carePlan.nurseRegularDutyKey}
                  onChange={handleCarePlanChange}
                  className={inputClass}
                  disabled={pricingLoading}
                >
                  <option value="" disabled hidden>
                    {pricingLoading
                      ? "Loading..."
                      : nurseRegularDutyOptions.length
                        ? "Select Duty Shift"
                        : "No priced regular duties"}
                  </option>
                  {nurseRegularDutyOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">
                  Service
                </label>
                <Select
                  styles={reactSelectStyles}
                  options={nurseRegularServiceOptions}
                  formatOptionLabel={(option, { context }) => {
                    if (context !== "menu") return option.label;
                    const notes = String(option?.notes || "").trim();
                    const services = safeArr(option?.services).filter(Boolean);
                    return (
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-800">
                          {option.label}
                        </span>
                        {services.length ? (
                          <span className="text-[11px] text-gray-600 mt-0.5">
                            <span className="font-medium text-gray-700">
                              Services:
                            </span>{" "}
                            {services.join(", ")}
                          </span>
                        ) : null}
                        {notes ? (
                          <span className="text-[11px] text-gray-600 mt-0.5">
                            <span className="font-medium text-gray-700">
                              Notes:
                            </span>{" "}
                            <span className="italic">{notes}</span>
                          </span>
                        ) : null}
                      </div>
                    );
                  }}
                  value={
                    nurseRegularServiceOptions.find(
                      (opt) => opt.value === carePlan.nurseRegularServiceKey,
                    ) || null
                  }
                  onChange={handleNurseRegularServiceChange}
                  isDisabled={
                    !carePlan.nurseSpecialist || !carePlan.nurseRegularDutyKey
                  }
                  placeholder={
                    !carePlan.nurseSpecialist
                      ? "Select specialist first"
                      : !carePlan.nurseRegularDutyKey
                        ? "Select duty shift first"
                        : nurseRegularServiceOptions.length
                          ? "Select service..."
                          : "No configured services for this shift"
                  }
                />
              </div>
              {selectedRegularSlot ? (
                <div className="md:col-span-2 rounded-xl border border-gray-200 bg-[#F6F8FC] px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-[13px] font-semibold text-gray-800 font-poppins">
                      {selectedRegularSlot.serviceName || "Selected Service"}
                    </p>
                    <button
                      type="button"
                      onClick={handleClearNurseRegularService}
                      className="inline-flex items-center justify-center w-6 h-6 rounded-full text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                      title="Clear selected service"
                      aria-label="Clear selected service"
                    >
                      <MdClose className="text-[15px]" />
                    </button>
                  </div>
                  <p className="text-[12px] font-medium text-gray-700 font-poppins mt-2">
                    Services included
                  </p>
                  <p className="text-[12px] text-gray-600 mt-1 font-poppins">
                    {safeArr(selectedRegularSlot.services).length
                      ? safeArr(selectedRegularSlot.services).join(", ")
                      : "No services configured"}
                  </p>
                  {String(selectedRegularSlot.notes || "").trim() ? (
                    <>
                      <p className="text-[12px] font-medium text-gray-700 mt-2 font-poppins">
                        Notes
                      </p>
                      <p className="text-[12px] text-gray-600 mt-1 font-poppins">
                        {selectedRegularSlot.notes}
                      </p>
                    </>
                  ) : null}
                </div>
              ) : null}
            </>
          )}

          {carePlan.nurseType === "One Time" && (
            <div className="md:col-span-2">
              <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">
                Services
              </label>
              <Select
                isMulti
                styles={reactSelectStyles}
                options={nurseOneTimeServiceOptions}
                formatOptionLabel={formatServiceOptionWithNotes}
                value={nurseOneTimeServiceOptions.filter((o) =>
                  (carePlan.nurseOneTimeServices || []).includes(o.value),
                )}
                onChange={handleNurseOneTimeServicesMulti}
                isDisabled={
                  pricingLoading || nurseOneTimeServiceOptions.length === 0
                }
                placeholder={
                  pricingLoading
                    ? "Loading..."
                    : nurseOneTimeServiceOptions.length
                      ? "Select one-time services..."
                      : "No priced one-time services"
                }
              />
            </div>
          )}

          {carePlan.nurseType && (
            <div className="md:col-span-2">
              <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">
                How many days?
              </label>
              <input
                type="number"
                name="nurseDays"
                value={carePlan.nurseDays}
                onChange={handleCarePlanChange}
                className={inputClass}
                placeholder="Enter number of days"
                min={1}
                max={99}
              />
            </div>
          )}
        </>
      )}

      {carePlan.serviceCategory ? (
        <div className="md:col-span-2">
          <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">
            Instruction for the patient
          </label>
          <textarea
            name="patientInstructions"
            value={carePlan.patientInstructions}
            onChange={handleCarePlanChange}
            rows={3}
            placeholder="Enter instructions for the patient"
            className={`${inputClass} resize-none`}
          />
        </div>
      ) : null}
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 bg-black/40 backdrop-blur-sm transition-opacity duration-300"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && isAddMode) {
          handleRequestClose();
        }
      }}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl h-[min(760px,92vh)] flex flex-col overflow-hidden transform scale-100 transition-transform duration-300"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
          <h2 className="text-[18px] font-semibold text-gray-800 font-poppins">
            {modalTitle}
          </h2>
          <button
            onClick={handleRequestClose}
            className="p-2 bg-gray-50 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
          >
            <MdClose size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto p-5 md:p-8 custom-scrollbar relative">
          {isAddMode ? renderEntryModeToggle() : null}

          {!isEditMode && !isBulkMode && renderStepIndicator()}
          {isBulkMode && renderBulkUpload()}

          {!isBulkMode &&
            (isEditMode || (!isPrescribeMode && currentStep === 1)) &&
            renderStep1()}
          {!isBulkMode && !isEditMode && currentStep === 2 && renderStep2()}
          {!isBulkMode && !isEditMode && currentStep === 3 && renderStep3()}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 flex justify-end gap-3 bg-gray-50/50">
          {isBulkMode ? (
            <>
              <button
                onClick={handleRequestClose}
                disabled={bulkSubmitting || bulkParsing}
                className="px-6 py-2.5 text-[13px] font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors font-poppins"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkSubmit}
                disabled={
                  bulkSubmitting ||
                  bulkParsing ||
                  bulkRows.length === 0 ||
                  bulkFileList.length === 0 ||
                  bulkErrors.length > 0
                }
                className="px-8 py-2.5 text-[13px] font-medium text-white bg-[#3E57E8] rounded-xl hover:bg-blue-700 shadow-md shadow-blue-500/20 transition-colors font-poppins disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {bulkSubmitting ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    Saving...
                  </span>
                ) : bulkParsing ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    Parsing...
                  </span>
                ) : (
                  "Upload & Save"
                )}
              </button>
            </>
          ) : isEditMode ? (
            <>
              <button
                onClick={handleRequestClose}
                className="px-6 py-2.5 text-[13px] font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors font-poppins"
              >
                Cancel
              </button>
              <button
                onClick={handleEditUpdate}
                className="px-8 py-2.5 text-[13px] font-medium text-white bg-[#3E57E8] rounded-xl hover:bg-blue-700 shadow-md shadow-blue-500/20 transition-colors font-poppins"
              >
                Update
              </button>
            </>
          ) : (
            <>
              {!isPrescribeMode && currentStep === 1 && (
                <button
                  onClick={handleRequestClose}
                  disabled={isUploadingDischargeSummary || isSubmittingCarePlan}
                  className="px-6 py-2.5 text-[13px] font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors font-poppins"
                >
                  Cancel
                </button>
              )}

              {currentStep > (isPrescribeMode ? 2 : 1) && (
                <button
                  onClick={() => setCurrentStep((prev) => prev - 1)}
                  disabled={isUploadingDischargeSummary || isSubmittingCarePlan}
                  className="px-6 py-2.5 text-[13px] font-medium text-[#3E57E8] bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors font-poppins"
                >
                  Back
                </button>
              )}

              {currentStep < 3 ? (
                <button
                  onClick={handleNext}
                  disabled={
                    isUploadingDischargeSummary ||
                    isCheckingPhone ||
                    isSubmittingCarePlan
                  }
                  className="px-8 py-2.5 text-[13px] font-medium text-white bg-[#3E57E8] rounded-xl hover:bg-blue-700 shadow-md shadow-blue-500/20 transition-colors font-poppins"
                >
                  {currentStep === 1 && isCheckingPhone ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                      Checking...
                    </span>
                  ) : currentStep === 2 && isUploadingDischargeSummary ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                      {isAddMode &&
                      !getDischargeUploadState().hasCompleteNewUpload
                        ? "Saving..."
                        : "Uploading..."}
                    </span>
                  ) : currentStep === 2 &&
                    isAddMode &&
                    !getDischargeUploadState().hasAnyUploadInput ? (
                    "Save & Close"
                  ) : (
                    "Next"
                  )}
                </button>
              ) : (
                <>
                  {isAddMode && currentStep === 3 && (
                    <button
                      onClick={handleSkipCarePlan}
                      disabled={
                        isUploadingDischargeSummary || isSubmittingCarePlan
                      }
                      className="px-6 py-2.5 text-[13px] font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors font-poppins"
                    >
                      Skip Care Plan
                    </button>
                  )}
                  <button
                    onClick={handleSubmit}
                    disabled={
                      isUploadingDischargeSummary ||
                      isSubmittingCarePlan ||
                      (Boolean(carePlan.serviceCategory) &&
                        !["ready", "not_required"].includes(
                          cancellationPolicyStatus,
                        ))
                    }
                    className="px-8 py-2.5 text-[13px] font-medium text-white bg-[#3E57E8] rounded-xl hover:bg-blue-700 shadow-md shadow-blue-500/20 transition-colors font-poppins"
                  >
                    {isSubmittingCarePlan ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                        Saving...
                      </span>
                    ) : isAddMode && isUploadingDischargeSummary ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                        Saving...
                      </span>
                    ) : (
                      "Submit"
                    )}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
      <PatientLocationPicker
        isOpen={isLocationPickerOpen}
        initialCoordinates={profile.coordinates}
        onClose={() => setIsLocationPickerOpen(false)}
        onConfirm={handleMapLocationSelected}
      />
      {isGoogleMapsLinkModalOpen ? (
        <div
          className="fixed inset-0 z-[140] flex items-center justify-center bg-black/50 p-4"
          onMouseDown={(event) => {
            if (
              event.target === event.currentTarget &&
              !isResolvingGoogleMapsLink
            ) {
              setIsGoogleMapsLinkModalOpen(false);
              setGoogleMapsLinkError("");
            }
          }}
        >
          <div
            className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
              <div>
                <h3 className="font-poppins text-[16px] font-semibold text-gray-800">
                  Google Map Link
                </h3>
                <p className="mt-1 font-poppins text-[11px] text-gray-500">
                  Paste a full Google Maps dropped-pin URL.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setIsGoogleMapsLinkModalOpen(false);
                  setGoogleMapsLinkError("");
                }}
                disabled={isResolvingGoogleMapsLink}
                className="rounded-full bg-gray-50 p-2 text-gray-500 disabled:opacity-50"
              >
                <MdClose size={20} />
              </button>
            </div>

            <div className="p-5">
              <label className="mb-1 block font-poppins text-[11px] font-medium text-gray-700">
                Google Maps full link
              </label>
              <input
                type="url"
                value={googleMapsLink}
                onChange={(event) => {
                  setGoogleMapsLink(event.target.value);
                  setGoogleMapsLinkError("");
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    handleApplyGoogleMapsLink();
                  }
                }}
                className={`${inputClass} ${googleMapsLinkError ? "border-red-500" : ""}`}
                placeholder="https://www.google.com/maps/..."
                autoComplete="off"
                autoFocus
              />
              {googleMapsLinkError ? (
                <p className="mt-2 font-poppins text-[10px] text-red-500">
                  {googleMapsLinkError}
                </p>
              ) : (
                <p className="mt-2 font-poppins text-[10px] text-gray-500">
                  Short maps.app.goo.gl links must be opened and expanded before
                  pasting.
                </p>
              )}
            </div>

            <div className="flex justify-end gap-2 border-t border-gray-100 bg-gray-50 px-5 py-4">
              <button
                type="button"
                onClick={() => {
                  setIsGoogleMapsLinkModalOpen(false);
                  setGoogleMapsLinkError("");
                }}
                disabled={isResolvingGoogleMapsLink}
                className="rounded-xl border border-gray-200 bg-white px-5 py-2.5 font-poppins text-[12px] text-gray-600 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApplyGoogleMapsLink}
                disabled={isResolvingGoogleMapsLink}
                className="rounded-xl bg-[#3E57E8] px-5 py-2.5 font-poppins text-[12px] font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isResolvingGoogleMapsLink
                  ? "Finding Address..."
                  : "Use Location"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
